import { Fragment, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  useAdvanceRunToPdpV1RunsRunIdAdvanceToPdpPost,
  useCancelRunV1RunsRunIdCancelPost,
  useGetJobV1JobsJobIdGet,
  useGetRunV1RunsRunIdGet,
  useRunStoreCoverageV1RunsRunIdStoreCoverageGet,
  useRunStoreSkuDetailV1RunsRunIdStoreCoverageStoreIdGet,
  useRunSweepStatsV1RunsRunIdSweepStatsGet,
  useSummaryV1ProxySummaryGet,
} from "../api/generated/qcsControlPlane";
import {
  type CartProgress,
  MANAGED_FLOWS,
  type Job,
  type Run,
  type RunStatus,
  attemptShare,
  crawlerAttempts,
  crawlerSucceeded,
  elapsedSeconds,
  fmtDateTimeSec,
  fmtDuration,
  fmtBytes,
  fmtInt,
  fmtLatencyPercentile,
  fmtMs,
  fmtPct,
  fmtRate,
  fmtRows,
  fmtTime,
  isCancellable,
  isTerminal,
  ANSWER_ATTEMPT_BUCKETS,
  LATENCY_CLASS_HELP,
  answersTotal,
  firstTryRate,
  latencyClasses,
  latencyMean,
  latencySamples,
  perSecond,
  rowsPerRequest,
  PROXY_REFUSAL_BANDS,
  attemptComposition,
  callComposition,
  retryEffectiveness,
  platformLabel,
  probeThroughput,
  statusTone,
  throughput,
} from "../api/labels";
import type { Slice } from "../api/labels";
import { useCanWrite } from "../auth/me";
import { useToast } from "../components/toast";
import { Badge, Button, cx, Dot, ErrorState, Grid, PageHeader, Panel, panelSurface, Row, Skeleton, SkeletonRegion, Stack, Tag, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, type DotTone as Tone } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Stat } from "../components/foundation";
import { InfoDot, PRESSURE_HELP } from "../components/InfoDot";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { AvailabilityRunPanel } from "../components/AvailabilityRunPanel";
import { RunCoverageMap } from "../components/RunCoverageMap";
import { StoreOverlapMap } from "../components/StoreOverlapMap";
import type { Platform } from "../api/generated/model";
import { TierPills } from "../components/tiers";
import { AnimatedNumber } from "../lib/motion";
import { ChevronDown, ChevronRight, CornerDownRight, ExternalLink } from "lucide-react";

// proxy summary shape (subset of the /v1/proxy/summary passthrough)
type ProxyPlatform = {
  platform: string;
  counts: Record<string, number>;
  total: number;
  leased: number;
  free: number;
  pressure: number;
  acquire_p50_ms: number;
  acquire_p99_ms: number;
};
type ProxySummary = { platforms: ProxyPlatform[] };

export function RunMonitor() {
  const { runId = "" } = useParams();
  const canWrite = useCanWrite();
  const qc = useQueryClient();
  const notify = useToast();

  // live progress pushed from SSE; overlays the polled run snapshot
  const [liveProgress, setLiveProgress] = useState<CartProgress | null>(null);
  const [liveStatus, setLiveStatus] = useState<RunStatus | null>(null);
  const [sseError, setSseError] = useState(false);
  // 1Hz tick so elapsed/throughput advance while running
  const [, setTick] = useState(0);

  const runQuery = useGetRunV1RunsRunIdGet(runId, {
    query: {
      // keep the legacy query key so the SSE handler's invalidate still matches
      queryKey: ["run", runId],
      // fall back to polling whenever SSE is broken or not yet terminal
      refetchInterval: (q) => {
        const r = q.state.data as Run | undefined;
        if (r && isTerminal(r.status)) return false;
        return sseError ? 3000 : 8000;
      },
    },
  });

  const run = runQuery.data as Run | undefined;
  const job = useJob(run?.job_id);

  // This run's OWN distinct-store count (per-run sightings), separate from the
  // progress `stores_found` counter, which reflects the anchor stage's rolling-window
  // catalog (all recent runs). Merchant runs only; refetches while live.
  const sweepStatsQuery = useRunSweepStatsV1RunsRunIdSweepStatsGet(runId, {
    query: {
      enabled: job?.flow === "merchant_sweep" && Boolean(run?.run_id),
      refetchInterval: () => {
        const r = runQuery.data as Run | undefined;
        return r && isTerminal(r.status) ? false : 8000;
      },
    },
  });
  const sweepStats = sweepStatsQuery.data;
  // breadcrumb trail: link back to the parent job when known, then this run.
  // The tab title leads with the job name - it identifies the run far better
  // than the id does - and keeps the short id after it.
  useBreadcrumbTail(
    job
      ? [{ label: job.name, to: `/jobs/${job.id}` }, { label: `Run ${runId.slice(0, 8)}` }]
      : [{ label: `Run ${runId.slice(0, 8)}` }],
    job ? [job.name, `Run ${runId.slice(0, 8)}`] : [`Run ${runId.slice(0, 8)}`],
  );

  // merge polled + live for the live status the rest of the view trusts
  const status: RunStatus | undefined = liveStatus ?? run?.status;
  const terminal = status ? isTerminal(status) : false;

  // -- SSE subscription -------------------------------------------------------
  useEffect(() => {
    if (!runId) return;
    setLiveProgress(null);
    setLiveStatus(null);
    setSseError(false);

    const es = new EventSource(`/v1/runs/${runId}/events`);

    const onProgress = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as {
          status?: RunStatus;
          progress?: CartProgress;
          error?: string | null;
        };
        if (data.progress) setLiveProgress(data.progress);
        if (data.status) {
          setLiveStatus(data.status);
          if (isTerminal(data.status)) {
            es.close();
            qc.invalidateQueries({ queryKey: ["run", runId] });
          }
        }
      } catch {
        /* ignore malformed frame */
      }
    };

    es.addEventListener("progress", onProgress as EventListener);
    es.addEventListener("error", () => {
      setSseError(true);
      es.close();
    });

    return () => es.close();
  }, [runId, qc]);

  // 1Hz clock while not terminal
  useEffect(() => {
    if (terminal) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [terminal]);

  const cancel = useCancelRunV1RunsRunIdCancelPost<Error>({
    mutation: {
      onSuccess: () => {
        notify("Cancellation requested");
        qc.invalidateQueries({ queryKey: ["run", runId] });
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const advance = useAdvanceRunToPdpV1RunsRunIdAdvanceToPdpPost<Error>({
    mutation: {
      onSuccess: () => {
        notify("Advancing to PDP: finishing search");
        qc.invalidateQueries({ queryKey: ["run", runId] });
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  if (runQuery.isError) {
    return (
      <>
        <ViewHeadFallback />
        <Panel>
          <Panel.Body tight>
            <ErrorState
              compact
              title="Could not load run"
              message={
                <>
                  Run <span className="mono">{runId}</span> could not be fetched.
                </>
              }
              onRetry={() => runQuery.refetch()}
            />
          </Panel.Body>
        </Panel>
      </>
    );
  }

  if (!run || !status) {
    return (
      <>
        <ViewHeadFallback />
        <Panel>
          <Panel.Body tight>
            <SkeletonRegion label="Loading run">
              <Stack gap="sm" className="p-4">
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          </Panel.Body>
        </Panel>
      </>
    );
  }

  // effective stats: prefer terminal summary, else live SSE, else polled snapshot.
  // The activity layer merges every heartbeat + the terminal write against a frozen
  // baseline of prior attempts (services/temporal/activities.py -> _progress_merge), so
  // the summary == the last merged live snapshot: no counter jumps backwards on a retry
  // and none jumps at completion. The bars/labels below can therefore trust the numbers.
  const stats: CartProgress =
    (terminal ? run.summary : null) ?? liveProgress ?? run.progress ?? {};
  const elapsed = elapsedSeconds({ ...run, status });
  const tone = statusTone(status);
  const live = !terminal;
  const isSweep = job?.flow === "brand_sweep";
  const isMerchant = job?.flow === "merchant_sweep";
  // The availability jobs read ClickHouse only: no stores, proxies or rows per second.
  const isAvailability = job !== undefined && MANAGED_FLOWS.has(job.flow);
  // store_sku_map reuses the cart-inventory engine + layout (the default `else` branch),
  // but its output is a carriage matrix and its resume cache is disabled (full re-probe
  // each run). Used only to tune the cart-flow cache panel copy.
  const isCarriage = job?.flow === "store_sku_map";
  const resolvedKnobs = run.resolved_knobs;
  // taxonomy classify: no proxies, no stores: just LLM batches over unlabelled SKUs.
  // Each SKU lands in exactly one terminal bucket (classified | unmapped | unknown) and
  // each batch increments batches_done once, so both ratios are <= 100% by construction,
  // no clamp. A value over 100% would be a genuine double-count worth seeing.
  const isTaxonomy = job?.flow === "taxonomy_classify";
  // Matching reads every platform at once and touches no store, proxy or request path,
  // so it shares none of the scrape-shaped panels below.
  const isMatch = job?.flow === "product_match";
  // shelf-id resolve: asks the platform for the per-(store, SKU) sellable id no sweep ever
  // stated. Its work is a SET of pairs, not a store x SKU rectangle, the pairs are exactly
  // the gaps search coverage left, so the cart header's "N stores x M SKUs = P pairs" has
  // no honest N or M to show and rendered "0 x 0". It gets its own header instead, built on
  // the split that actually matters here and that no other panel shows: an id we were given
  // vs the platform stating it has no such shelf row. Both are answers; only one is an id.
  const isShelfIds = job?.flow === "shelf_id_resolve";
  const shelfTotal = stats.pairs_total ?? 0;
  const shelfResolved = stats.pairs_resolved ?? 0;
  const shelfAbsent = stats.pairs_absent ?? 0;
  const shelfAnswered = shelfResolved + shelfAbsent;
  // The re-check cut of those answers (the "refresh" worklist): pairs we already held an
  // id for, and what became of it. `moved` is the actionable half - a changed id means
  // every cart reading since has read a stocked SKU as absent.
  const shelfRechecked = stats.pairs_rechecked ?? 0;
  const shelfChanged = stats.pairs_changed ?? 0;
  const shelfLost = stats.pairs_lost ?? 0;
  const shelfRestored = stats.pairs_restored ?? 0;
  const shelfMoved = shelfChanged + shelfLost;
  // How the worklist splits (a partition of pairs_total). Absent on runs from before it
  // was published, which is why the row is conditional rather than showing two zeros.
  const shelfNew = stats.pairs_new ?? 0;
  const shelfReask = stats.pairs_reask ?? 0;
  const shelfMixTotal = shelfNew + shelfReask;
  const shelfPct = shelfTotal > 0 ? (shelfAnswered / shelfTotal) * 100 : 0;
  // Bandwidth, split by the pass that spent it. The prepass is a bet that one search page
  // costs less than the PDPs it replaces, and this is the only number that settles it,
  // leases and rows say how much was harvested, not what it cost to harvest. `null` (not 0)
  // when the run predates the counters, so an old run reads "not measured" rather than
  // claiming it used no network.
  const searchDown = stats.search_bytes_down;
  const pdpDown = stats.pdp_bytes_down;
  const shelfSplit = searchDown != null && pdpDown != null;
  // Only worth splitting when the prepass actually ran; otherwise it is the PDP pass alone
  // and a "search: 0 B" half says nothing.
  const shelfPrepassRan = (stats.search_stores_total ?? 0) > 0 || (searchDown ?? 0) > 0;
  // search / category rank: the work unit is a PROBE, one shelf asked at one dark store,
  // answered by a page of slots. There is no store x SKU rectangle, so every store_sku_*
  // counter the default panels read is absent -- they rendered "15 / 0 pairs" and a
  // pending of -15. Rank gets its own header, coverage panel and resume bar instead.
  const isRank = job?.flow === "search_rank" || job?.flow === "category_rank";
  const rankShelfNoun = job?.flow === "category_rank" ? "categories" : "keywords";
  const probesPlanned = stats.probes_planned ?? 0;
  const probesOk = stats.probes_ok ?? 0;
  const probesDone = stats.probes_done ?? 0;
  const probesFailed = stats.probes_failed ?? 0;
  // Answered, not merely attempted: a probe that came back "nothing there" measured the
  // shelf and a failed one did not, which is exactly the split coverage is defined on.
  //
  // Read off `probes_done` rather than summed from the three outcome counters, because a
  // probe RESUMED from an earlier attempt of this run increments `probes_done` and none of
  // them -- it was answered, just not by this leg. Summed, a run that resumed everything
  // shows nothing measured beside a 100% coverage badge.
  const probesAnswered = probesDone - probesFailed;
  const probePct = probesPlanned > 0 ? (probesAnswered / probesPlanned) * 100 : 0;
  const rankShelves = stats.shelves_total ?? 0;
  const rankStores = stats.stores_total ?? 0;
  const slotsWritten = stats.placements_written ?? stats.rows_emitted ?? 0;
  // The run's own verdict on itself. Published only once the run finished, so a live run
  // shows the bar and no badge rather than a coverage that is still climbing.
  const coveragePctReported = stats.coverage_pct;
  const coverageThresholdPct = stats.coverage_threshold_pct;
  const taxoTotal = stats.total ?? 0;
  const taxoClassified = stats.classified ?? 0;
  const taxoDone = taxoClassified + (stats.unmapped ?? 0) + (stats.unknown ?? 0);
  const taxoPct = taxoTotal > 0 ? (taxoDone / taxoTotal) * 100 : 0;
  const taxoBatchesTotal = stats.batches_total ?? 0;
  const taxoBatchesDone = stats.batches_done ?? 0;
  const taxoBatchPct = taxoBatchesTotal > 0 ? (taxoBatchesDone / taxoBatchesTotal) * 100 : 0;
  // Merchant sweep: throughput = live serviceability probe calls/s (cache hits make
  // no network call, so they're excluded). Other flows: rows written/s.
  const rps = isMerchant ? probeThroughput(stats, elapsed) : throughput(stats, elapsed);

  // merchant sweep: progress = cells *successfully* probed (once each; retries/failures
  // excluded), so the bar is bounded by cells_total instead of overflowing on retry churn.
  const storesTotal = isMerchant ? (stats.cells_total ?? 0) : (stats.stores_total ?? 0);
  const storesDone = isMerchant ? (stats.cells_succeeded ?? 0) : (stats.stores_processed ?? 0);
  // Percentages are RAW (unclamped) so the numeric label surfaces a >100% double-count
  // instead of hiding it, the backend retry-merge keeps these bounded (coverage counters
  // are MAX-FLOOR <= total), so a >100% here is a real bug worth seeing, exactly like the
  // taxonomy ratios above. Only the bar WIDTH is clamped (a >100% bar overflows layout).
  const storePct = storesTotal > 0 ? (storesDone / storesTotal) * 100 : 0;
  // brand sweep headline: (store x brand-candidate) searches that SUCCESSFULLY finished,
  // not stores swept. Each unit ends in exactly one of ok/failed; the denominator is the
  // planned roots (~= brands x stores). The headline number is ok-only (the ask), so a run
  // with failures stays below 100% -- the gap is the failed segment, rendered crit on the
  // bar the same way SweepStagePanel does, so ok+failed together reach 100% at completion.
  const searchUnitsTotal = stats.search_units_total ?? 0;
  const searchUnitsOk = stats.search_units_ok ?? 0;
  const searchUnitsFailed = stats.search_units_failed ?? 0;
  const searchUnitsPct = searchUnitsTotal > 0 ? (searchUnitsOk / searchUnitsTotal) * 100 : 0;
  const searchUnitsFailedPct = searchUnitsTotal > 0 ? (searchUnitsFailed / searchUnitsTotal) * 100 : 0;

  // brand-sweep store-sampling badge (planning/23 [08] B1). Prefer the run's EFFECTIVE
  // resolved mode (stamped by dispatch on the summary); fall back to the job's mode knob
  // for a live run before the summary lands; else "full" (the engine default) so legacy
  // knobs={} jobs still label correctly rather than showing nothing.
  const sweepMode =
    stats.sample_mode ?? (job?.knobs?.mode as string | undefined) ?? "full";
  const sweepSampleK = stats.sample_k ?? (job?.knobs?.store_sample as number | undefined);
  const sweepActiveTotal = stats.active_stores_total;
  const sweepModeBadge = !isSweep
    ? null
    : sweepMode === "sampled"
      ? {
          tone: "info",
          label: `Sampled${sweepSampleK ? ` - ${fmtInt(sweepSampleK)}${sweepActiveTotal ? `/${fmtInt(sweepActiveTotal)}` : ""} stores` : ""}`,
          title:
            "Sampled: a rotating random store subset, additive (removals happen via the rolling window once recent runs cover every store, or on a Full run). Coverage per run is partial.",
        }
      : {
          tone: "warn",
          label: "Full - removes",
          title: "Full: searches every store and retires SKUs it no longer finds (authoritative).",
        };

  // brand-sweep coverage: products discovered + PDP-enrich progress
  const productsFound = stats.products_discovered ?? 0;
  const pdpsTotal = stats.pdps_total ?? 0;
  const pdpsDone = stats.pdps_done ?? 0;
  const pdpPct = pdpsTotal > 0 ? (pdpsDone / pdpsTotal) * 100 : 0; // raw; width clamped at the bar
  // Search-only platforms (Instamart) have no PDP stage -> hide the PDP
  // bar/rows + "Advance to PDP". Undefined (other platforms / older runs) => has PDP.
  const hasPdpStage = stats.has_pdp_stage !== false;

  // Store-SKU coverage is the unit of work: each unit is one SKU checked at one
  // dark store (a batch splits per partial response): written (= rows_emitted) /
  // deduped / gave-up / not-serviceable / pending, of store_sku_total.
  const storeSkuTotal = stats.store_sku_total ?? 0;
  const storeSkuWritten = stats.rows_emitted ?? 0;
  const coveragePct = storeSkuTotal > 0 ? (storeSkuWritten / storeSkuTotal) * 100 : 0; // raw; width clamped at the bar
  // store_sku_total = dark stores x SKUs, so SKUs-per-store = total / stores.
  const skusPerStore = storesTotal > 0 ? Math.round(storeSkuTotal / storesTotal) : 0;

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title={
          <>
            Run{" "}
            {run.run_id ? (
              <span className="num" style={{ color: "var(--ink-2)" }}>#{run.run_id}</span>
            ) : (
              <span
                className="num"
                style={{ color: "var(--ink-3)", fontStyle: "italic" }}
                title="This run was queued but a worker never started it, so it never got a run ID."
              >
                not started
              </span>
            )}
          </>
        }
        titleMeta={
          <>
            <Badge variant={toneVariant(tone)} style={{ fontSize: 11.5 }}>
              {live && <Dot live />}
              {status}
            </Badge>
            {job && !isMatch && (
              <span className={`plat ${job.platform}`}>{platformLabel(job.platform)}</span>
            )}
            {job?.ad_hoc && (
              <Badge
                variant="outline-info"
                style={{ fontSize: 11 }}
                title="A one-off, scope-pinned launch from the brand page (not a scheduled job)"
              >
                ad-hoc
              </Badge>
            )}
            {isSweep && sweepModeBadge && (
              <Badge
                variant={toneVariant(sweepModeBadge.tone as Tone)}
                style={{ fontSize: 11 }}
                title={sweepModeBadge.title}
              >
                {sweepModeBadge.label}
              </Badge>
            )}
            {isMerchant && <StageTracker stage={stats.stage} status={status} live={live} />}
            {sseError && !terminal && (
              <Badge variant="outline-warning" title="SSE dropped - polling fallback active">polling</Badge>
            )}
          </>
        }
        description={
          <span className="inline-flex flex-wrap gap-x-[18px] font-mono text-[11.5px]">
            <span>trigger <b style={{ color: "var(--ink-2)" }}>{run.trigger}</b></span>
            <span>started <b style={{ color: "var(--ink-2)" }}>{fmtDateTimeSec(run.started_at)}</b></span>
            <span>elapsed <b style={{ color: "var(--ink-2)" }}>{fmtDuration(elapsed)}</b></span>
            <span>attempts <b style={{ color: "var(--ink-2)" }}>{run.attempts}</b></span>
            <span>run_id <b style={{ color: "var(--info)" }}>{run.run_id ?? "-"}</b></span>
          </span>
        }
        action={canWrite ? (
          <Row gap="sm" className="flex-wrap justify-end">
          {isSweep && hasPdpStage && isCancellable(status) && pdpsTotal === 0 && (
            <Button
              disabled={advance.isPending || run.advance_requested || run.cancel_requested}
              onClick={() => advance.mutate({ runId })}
              title="Stop searching, enrich what was found, and finish normally"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14 }}>
                <path d="M4 4l10 8-10 8V4zm12 0h3v16h-3V4z" />
              </svg>
              {run.advance_requested ? "Advancing..." : "Advance to PDP"}
            </Button>
          )}
          <Button
            variant="destructive"
            disabled={!isCancellable(status) || cancel.isPending || run.cancel_requested}
            onClick={() => cancel.mutate({ runId })}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14 }}>
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
            {run.cancel_requested ? "Stopping..." : "Stop run"}
          </Button>
          </Row>
        ) : undefined}
      />

      {isAvailability ? (
        <AvailabilityRunPanel stats={stats} live={live} elapsed={elapsed} />
      ) : (
        <>
        {/* KPI row */}
        <Grid cols={3} gap="md" className="mb-3.5">
          {isMatch ? (
            // Matching issues no requests, so rows/s and dedup say nothing about it. What
            // does: how much catalogue it read, and how much of it it had to write back.
            <>
              <Stat
                surface
                label="listings read"
                value={<AnimatedNumber value={stats.listings ?? 0} format={fmtRows} />}
                tone="brand"
                foot={<span className="faint">across every platform</span>}
              />
              <Stat
                surface
                label="membership rows written"
                value={stats.rows_emitted == null ? "-" : <AnimatedNumber value={stats.rows_emitted} format={fmtRows} />}
                tone="ok"
                foot={<span className="faint">one per listing, naming its SKU</span>}
              />
            </>
          ) : (
            <>
              <Stat
                surface
                label={<>{live && <Dot live />} throughput</>}
                value={rps == null ? "-" : <AnimatedNumber value={rps} format={(n) => Math.round(n).toLocaleString()} />}
                unit={isMerchant ? "probes/s" : "rows/s"}
                tone="brand"
                foot={<span className="faint">{isMerchant ? "live probes / elapsed" : "rows / elapsed"}</span>}
              />
              <Stat
                surface
                // Rank writes one row per visible card and dedupes nothing, so the generic
                // "rows emitted / deduped 0" says less than the flow's own word for them.
                label={isRank ? "slots recorded" : "rows emitted"}
                value={stats.rows_emitted == null ? "-" : <AnimatedNumber value={stats.rows_emitted} format={fmtRows} />}
                tone="ok"
                foot={
                  <span className="faint">
                    {isRank
                      ? probesOk > 0
                        ? `${(slotsWritten / probesOk).toFixed(0)} slots per shelf that returned cards`
                        : "one row per visible card, in the order the platform returned them"
                      : `deduped ${fmtInt(stats.rows_deduped)}`}
                  </span>
                }
              />
            </>
          )}
          {isMerchant ? (
            <>
              <Stat
                surface
                label="stores this run"
                value={<AnimatedNumber value={sweepStats?.stores ?? 0} format={fmtRows} />}
                tone="ok"
                foot={
                  <span className="faint">
                    {fmtInt(sweepStats?.serviceable_cells ?? 0)} serviceable cells - distinct this run
                  </span>
                }
              />
              <Stat
                surface
                label="catalog (anchored)"
                value={stats.stores_found == null ? "-" : <AnimatedNumber value={stats.stores_found} format={fmtRows} />}
                foot={<span className="faint">{fmtInt(stats.stores_new)} new - rolling-window union</span>}
              />
            </>
          ) : isSweep ? (
            <div className={cx("kpi", panelSurface)}>
              <div className="label">products found</div>
              <div className="value ok num"><AnimatedNumber value={productsFound} format={fmtRows} /></div>
              {hasPdpStage && (
                <div className="foot">
                  <div className="bar" style={{ width: 130 }}>
                    <span className={`ok${live ? " stream" : ""}`} style={{ width: `${Math.min(100, pdpPct)}%` }} />
                  </div>
                  <span className="num faint">{fmtInt(pdpsDone)}/{fmtInt(pdpsTotal)} PDP</span>
                </div>
              )}
            </div>
          ) : isMatch ? (
            <div className={cx("kpi", panelSurface)}>
              <div className="label">SKUs on 2+ platforms</div>
              <div className="value ok num">
                <AnimatedNumber value={stats.cross_platform ?? 0} format={fmtRows} />
                <small> / {fmtRows(stats.groups ?? 0)}</small>
              </div>
              <div className="foot">
                <span className="faint">matched across platforms / distinct SKUs found</span>
              </div>
            </div>
          ) : isTaxonomy ? (
            <div className={cx("kpi", panelSurface)}>
              <div className="label">SKUs classified</div>
              <div className="value ok num">
                <AnimatedNumber value={taxoClassified} format={fmtRows} /><small> / {fmtRows(taxoTotal)}</small>
              </div>
              <div className="foot">
                <div className="bar" style={{ width: 130 }}>
                  <span className={`ok${live ? " stream" : ""}`} style={{ width: `${taxoPct}%` }} />
                </div>
                <span className="num faint">{taxoPct.toFixed(0)}% resolved</span>
              </div>
            </div>
          ) : isRank ? (
            <div className={cx("kpi", panelSurface)}>
              <div className="label">shelves measured</div>
              <div className="value num">
                <AnimatedNumber value={probesAnswered} format={fmtRows} />
                <small> / {fmtRows(probesPlanned)}</small>
              </div>
              <div className="foot" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="num faint" style={{ fontSize: 10.5 }}>
                  {fmtInt(rankShelves)} {rankShelfNoun} x {fmtInt(rankStores)} stores ={" "}
                  {fmtInt(probesPlanned)} probes
                </span>
                <span className="row" style={{ gap: 8 }}>
                  <div className="bar" style={{ width: 130 }}>
                    <span className={`ok${live ? " stream" : ""}`} style={{ width: `${Math.min(100, probePct)}%` }} />
                  </div>
                  <span className="num faint">{probePct.toFixed(0)}%</span>
                </span>
              </div>
            </div>
          ) : isShelfIds ? (
            <div className={cx("kpi", panelSurface)}>
              <div className="label">shelf ids answered</div>
              <div className="value num">
                <AnimatedNumber value={shelfAnswered} format={fmtRows} /><small> / {fmtRows(shelfTotal)}</small>
              </div>
              <div className="foot" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="num faint" style={{ fontSize: 10.5 }}>
                  {fmtInt(shelfTotal)} store-SKU pairs in this pass
                </span>
                {/* How the worklist splits. A re-fetch and an ordinary top-up are the same
                    flow over the same scope, so only the run can say which this one is. */}
                {shelfMixTotal > 0 && (
                  <span className="num faint" style={{ fontSize: 10.5 }}>
                    {fmtInt(shelfNew)} never answered
                    {shelfReask > 0 ? ` - ${fmtInt(shelfReask)} re-fetched` : ""}
                  </span>
                )}
                <span className="row" style={{ gap: 8 }}>
                  <div className="bar" style={{ width: 130 }}>
                    <span className={`ok${live ? " stream" : ""}`} style={{ width: `${Math.min(100, shelfPct)}%` }} />
                  </div>
                  <span className="num faint">{shelfPct.toFixed(0)}%</span>
                </span>
              </div>
            </div>
          ) : (
            <div className={cx("kpi", panelSurface)}>
              <div className="label">SKU x dark-store coverage</div>
              <div className="value num">
                <AnimatedNumber value={storeSkuWritten} format={fmtRows} /><small> / {fmtRows(storeSkuTotal)}</small>
              </div>
              <div className="foot" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="num faint" style={{ fontSize: 10.5 }}>
                  {fmtInt(storesTotal)} dark stores x {fmtInt(skusPerStore)} SKUs ={" "}
                  {fmtInt(storeSkuTotal)} pairs
                </span>
                <span className="row" style={{ gap: 8 }}>
                  <div className="bar" style={{ width: 130 }}>
                    <span className={`ok${live ? " stream" : ""}`} style={{ width: `${Math.min(100, coveragePct)}%` }} />
                  </div>
                  <span className="num faint">{coveragePct.toFixed(0)}%</span>
                </span>
              </div>
            </div>
          )}
          {isMatch ? (
            <>
              <div className={cx("kpi", panelSurface)}>
                <div className="label">brands matched</div>
                <div className="value num">
                  <AnimatedNumber value={stats.brands ?? 0} format={fmtInt} />
                </div>
                <div className="foot">
                  <span className="faint">{fmtInt(stats.listings ?? 0)} listings read</span>
                </div>
              </div>
              <div className={cx("kpi", panelSurface)}>
                <div className="label">pairs judged</div>
                <div className="value num">
                  <AnimatedNumber value={stats.judged ?? 0} format={fmtInt} />
                </div>
                <div className="foot">
                  <span className="faint">only the ambiguous band reaches the model</span>
                </div>
              </div>
            </>
          ) : isTaxonomy ? (
            <>
              <div className={cx("kpi", panelSurface)}>
                <div className="label">batches</div>
                <div className="value num">
                  <AnimatedNumber value={taxoBatchesDone} format={fmtInt} /><small> / {fmtInt(taxoBatchesTotal)}</small>
                </div>
                <div className="foot">
                  <div className="bar" style={{ width: 130 }}>
                    <span className={`ok${live ? " stream" : ""}`} style={{ width: `${taxoBatchPct}%` }} />
                  </div>
                  <span className="num faint">{taxoBatchPct.toFixed(0)}%</span>
                </div>
              </div>
              <div className={cx("kpi", panelSurface)}>
                <div className="label">unmapped / retry</div>
                <div className="value num">
                  {stats.unmapped == null ? "-" : <AnimatedNumber value={stats.unmapped} format={fmtInt} />}<small> / {fmtInt(stats.unknown)}</small>
                </div>
                <div className="foot">
                  <span className="faint">no-fit leaf - model-omitted (retried next run)</span>
                </div>
              </div>
            </>
          ) : isRank ? (
            <>
              {/* Rank has no store x SKU stage panel: its work axis is the probe, and the
                  header KPI already carries it. Bandwidth alone here. */}
              <Stat
                surface
                label="bandwidth"
                value={stats.bytes_down == null ? "-" : <AnimatedNumber value={stats.bytes_down} format={fmtBytes} />}
                unit="down"
                foot={<span className="faint">{fmtBytes(stats.bytes_up)} up</span>}
              />
            </>
          ) : isShelfIds ? (
            <>
              {/* The whole point of the pass, and the number no shared panel renders: of the
                  pairs the platform ANSWERED, how many carry an id. A low share is not a
                  failure - most stores carry most SKUs at neither, so a stated absence is
                  the majority answer and is itself worth recording. */}
              <div className={cx("kpi", panelSurface)}>
                <div className="label">ids stated</div>
                <div className="value ok num">
                  <AnimatedNumber value={shelfResolved} format={fmtRows} />
                  <small> / {fmtRows(shelfAnswered)} answered</small>
                </div>
                <div className="foot">
                  <span className="faint">
                    {fmtInt(shelfAbsent)} stated no shelf row
                    {shelfAnswered > 0 && ` - ${((shelfResolved / shelfAnswered) * 100).toFixed(0)}% carry an id`}
                  </span>
                </div>
              </div>
              {/* Re-confirmation: of the pairs this pass already had an id for, how many
                  still hold it. A change here is not a smaller version of the panel above,
                  it says the cart has been addressing an id the platform no longer honours,
                  and every reading taken against it read a stocked SKU as absent. Rendered
                  whenever the pass re-checked anything, including at zero: "nothing moved"
                  is the answer the re-check exists to give. */}
              {shelfRechecked > 0 && (
                <div className={cx("kpi", panelSurface)}>
                  <div className="label">ids re-confirmed</div>
                  <div className={`value num${shelfMoved > 0 ? " crit" : " ok"}`}>
                    <AnimatedNumber value={shelfRechecked - shelfMoved} format={fmtRows} />
                    <small> / {fmtRows(shelfRechecked)} re-asked</small>
                  </div>
                  <div className="foot">
                    <span className="faint">
                      {shelfMoved === 0
                        ? "no id moved"
                        : `${fmtInt(shelfChanged)} changed id - ${fmtInt(shelfLost)} shelf row gone`}
                    </span>
                  </div>
                </div>
              )}
              {stats.pairs_restored != null && (
                <div className={cx("kpi", panelSurface)}>
                  <div className="label">confirmed absent to shelf ID</div>
                  <div className="value ok num">
                    <AnimatedNumber value={shelfRestored} format={fmtRows} />
                  </div>
                  <div className="foot">
                    <span className="faint">previously absent pairs now have an ID</span>
                  </div>
                </div>
              )}
              <Stat
                surface
                label="bandwidth"
                value={stats.bytes_down == null ? "-" : <AnimatedNumber value={stats.bytes_down} format={fmtBytes} />}
                unit="down"
                foot={
                  <span className="faint">
                    {stats.bytes_down == null
                      ? "not measured on this flow"
                      : shelfSplit && shelfPrepassRan
                        ? `${fmtBytes(searchDown)} search - ${fmtBytes(pdpDown)} pdp`
                        : `${fmtBytes(stats.bytes_up)} up`}
                  </span>
                }
              />
            </>
          ) : (
            <>
              <div className={cx("kpi", panelSurface)}>
                <div className="label">
                  {isMerchant ? `cells probed${stats.phase ? ` - ${stats.phase}` : ""}` : isSweep ? "store x brand searches" : "stores processed"}
                </div>
                <div className="value num">
                  <AnimatedNumber value={isSweep ? searchUnitsOk : storesDone} format={fmtInt} />
                  <small> / {fmtInt(isSweep ? searchUnitsTotal : storesTotal)}</small>
                </div>
                <div className="foot">
                  <div className="bar" style={{ width: 130 }}>
                    <span
                      className={`ok${live ? " stream" : ""}`}
                      style={{ width: `${Math.min(100, isSweep ? searchUnitsPct : storePct)}%` }}
                    />
                    {isSweep && searchUnitsFailed > 0 && (
                      <span
                        className="crit"
                        style={{ width: `${searchUnitsFailedPct}%`, left: `${Math.min(100, searchUnitsPct)}%` }}
                      />
                    )}
                  </div>
                  <span className="num faint">
                    {isMerchant
                      ? `${fmtInt(stats.cells_serviceable)} serviceable`
                      : `${(isSweep ? searchUnitsPct : storePct).toFixed(0)}%`}
                  </span>
                </div>
              </div>
              <Stat
                surface
                label="bandwidth"
                value={stats.bytes_down == null ? "-" : <AnimatedNumber value={stats.bytes_down} format={fmtBytes} />}
                unit="down"
                foot={<span className="faint">{fmtBytes(stats.bytes_up)} up</span>}
              />
            </>
          )}
          <Stat
            surface
            label={<>{live && <Dot live />} run time</>}
            value={fmtDuration(elapsed)}
            foot={<span className="faint">{terminal ? "total" : "elapsed"}</span>}
          />
        </Grid>
        </>
      )}

      {resolvedKnobs && <ConfigUsedPanel knobs={resolvedKnobs} />}

      {!isAvailability && (
        <>
        {/* merchant sweep: live geographic scan progress */}
        {isMerchant && (
          <Grid cols={12} gap="md" className="mb-3.5">
            <RunCoverageMap runId={run.id} live={live} />
          </Grid>
        )}

        {/* detail split. Taxonomy classify has no proxies/stores, so it gets a single
            classification-breakdown panel instead of the coverage/churn/proxy trio. */}
        {isMatch ? (
          <Grid cols={12} gap="md" className="mb-3.5 items-start">
            <ProductMatchRunPanel stats={stats} />
          </Grid>
        ) : isTaxonomy ? (
          <Grid cols={12} gap="md" className="mb-3.5 items-start">
            <TaxonomyRunPanel stats={stats} live={live} />
          </Grid>
        ) : (
          <>
            <Grid cols={12} gap="md" className="mb-3.5 items-start">
              {/* Every flow that leases a proxy reports the same request axis, so the
                  pipeline panel rides beside whichever stage panel the flow uses for its own
                  work unit. It renders only the blocks a given run can honestly fill. */}
              {isMerchant ? (
                <Stack gap="md" style={{ gridColumn: "span 5" }}>
                  <MerchantPanel stats={stats} live={live} />
                  <PipelinePanel stats={stats} elapsed={elapsed} live={live} />
                </Stack>
              ) : isSweep ? (
                <Stack gap="md" style={{ gridColumn: "span 5" }}>
                  <SweepStagePanel stats={stats} live={live} />
                  <PipelinePanel stats={stats} elapsed={elapsed} live={live} />
                </Stack>
              ) : (
                // Coverage and the pipeline rates share the column: coverage is short and the
                // churn panel beside it is tall, so stacking them fills the gap that a
                // grid-placed sibling would leave under coverage instead.
                <Stack gap="md" style={{ gridColumn: "span 5" }}>
                  {isRank ? (
                    <RankCoveragePanel
                      stats={stats}
                      shelfNoun={rankShelfNoun}
                      coveragePct={coveragePctReported}
                      thresholdPct={coverageThresholdPct}
                    />
                  ) : (
                    <CoveragePanel
                      stats={stats}
                      title={isShelfIds ? "Pair coverage" : undefined}
                    />
                  )}
                  <PipelinePanel stats={stats} elapsed={elapsed} live={live} />
                </Stack>
              )}
              {/* Only once the prepass has published: a run without one shows nothing extra. */}
              {isShelfIds && (stats.search_stores_total ?? 0) > 0 && (
                <SearchPrepassPanel stats={stats} live={live} />
              )}
              {isMerchant ? (
                <MerchantCachePanel stats={stats} />
              ) : isSweep ? (
                <SweepCachePanel stats={stats} mode={sweepMode} />
              ) : (
                <ChurnPanel stats={stats} unit={isRank ? "ask" : "batch"} />
              )}
              {/* retry-churn / outcome attribution: cart flows render it in the slot above;
                  brand + merchant get their own outcome panel here so every flow shows one. */}
              {isSweep && <ChurnPanel stats={stats} unit="request" />}
              {isMerchant && <MerchantChurnPanel stats={stats} />}
              {/* read-through cache hit-rate: brand/merchant have their own cache panels above;
                  cart flows get the resume-cache panel here. */}
              {!isSweep && !isMerchant && (
                <ResumeCachePanel stats={stats} carriage={isCarriage} rank={isRank} />
              )}
              {job && <ProxyPanel platform={job.platform} />}
            </Grid>

            {/* completed cart runs: per-store coverage with per-SKU drill-down */}
            {terminal && !isSweep && !isMerchant && !isRank && (
              <Grid cols={12} gap="md" className="mb-3.5">
                <StoreCoveragePanel runId={run.id} platform={job?.platform ?? null} target={skusPerStore} />
              </Grid>
            )}
          </>
        )}
        </>
      )}

      {/* lifecycle + summary/error */}
      <Grid cols={12} gap="md" className="items-start">
        <LifecyclePanel run={run} status={status} />
        <Panel style={{ gridColumn: "span 7" }}>
          <Panel.Head title={terminal ? "Summary" : "Run lineage"}>
            <div className="flex-1" />
            {live && (
              <Badge variant="outline-success"><Dot live /> {sseError ? "polling" : "streaming - SSE"}</Badge>
            )}
          </Panel.Head>
          <Panel.Body>
            {run.error && (
              <div
                className="mono"
                style={{
                  color: "var(--crit)",
                  background: "var(--crit-dim)",
                  border: "1px solid var(--crit-line)",
                  borderRadius: "var(--r)",
                  padding: "10px 12px",
                  marginBottom: 14,
                  fontSize: 11.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {run.error}
              </div>
            )}
            {status === "degraded" && run.summary?.degraded_reason && (
              <div
                className="mono"
                style={{
                  color: "var(--warn)",
                  background: "var(--warn-dim)",
                  border: "1px solid var(--warn-line)",
                  borderRadius: "var(--r)",
                  padding: "10px 12px",
                  marginBottom: 14,
                  fontSize: 11.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                DEGRADED, {run.summary?.degraded_reason}. The run completed, but coverage
                fell short (expected when the platform blocks most requests).
              </div>
            )}
            <div className="kv">
              <dt>run_id</dt>
              <dd style={{ color: "var(--info)" }}>{run.run_id ?? "-"}</dd>
              <dt>trigger</dt>
              <dd>{run.trigger}</dd>
              <dt>claimed by</dt>
              <dd>{run.claimed_by ?? "-"}</dd>
              <dt>started</dt>
              <dd>{fmtDateTimeSec(run.started_at)}</dd>
              <dt>finished</dt>
              <dd>{fmtDateTimeSec(run.finished_at)}</dd>
              <dt>attempts</dt>
              <dd>{run.attempts}</dd>
              {terminal && isSweep && (
                <>
                  <dt>products discovered</dt>
                  <dd>
                    {fmtInt(stats.products_discovered)}
                    {sweepActiveTotal ? (
                      <span className="faint" style={{ fontSize: 10.5 }}>
                        {" "}- {fmtInt(storesTotal)}/{fmtInt(sweepActiveTotal)} stores
                        {sweepMode === "sampled" ? " (sampled)" : ""}
                      </span>
                    ) : null}
                  </dd>
                  <dt>SKUs added / removed</dt>
                  <dd>
                    {stats.remove_missing === false ? (
                      <>
                        {fmtInt(stats.skus_added)} added{" "}
                        <span className="faint" style={{ fontSize: 10.5 }}>
                          - additive (removals via rolling window / Full run)
                        </span>
                      </>
                    ) : (
                      <>
                        {fmtInt(stats.skus_added)} / {fmtInt(stats.skus_removed)}
                        {sweepMode === "sampled" && (stats.rolling_window_runs ?? 0) > 0 ? (
                          <span className="faint" style={{ fontSize: 10.5 }}>
                            {" "}- rolling window ({stats.rolling_window_runs} runs)
                          </span>
                        ) : null}
                      </>
                    )}
                  </dd>
                </>
              )}
              {terminal && isTaxonomy && (
                <>
                  <dt>classified / unmapped</dt>
                  <dd>{fmtInt(stats.classified)} / {fmtInt(stats.unmapped)}</dd>
                  <dt>unknown (retried next run)</dt>
                  <dd>{fmtInt(stats.unknown)}</dd>
                </>
              )}
              {terminal && isRank && (
                <>
                  <dt>coverage</dt>
                  <dd>
                    {coveragePctReported == null ? "-" : `${coveragePctReported}%`}
                    {coverageThresholdPct != null && (
                      <span className="faint" style={{ fontSize: 10.5 }}>
                        {" "}- job threshold {coverageThresholdPct}%
                      </span>
                    )}
                  </dd>
                  <dt>shelves never answered</dt>
                  <dd>{fmtInt(stats.probes_failed)}</dd>
                </>
              )}
              {terminal && !isSweep && !isTaxonomy && !isRank && !isAvailability && (
                <>
                  <dt>rows deduped</dt>
                  <dd>{fmtInt(stats.rows_deduped)}</dd>
                  <dt>stores not serviceable</dt>
                  <dd>{fmtInt(stats.stores_not_serviceable)}</dd>
                </>
              )}
            </div>
          </Panel.Body>
        </Panel>
      </Grid>
    </>
  );
}

// -- sub-panels ---------------------------------------------------------------
function formatKnobValue(value: unknown): string {
  if (value == null) return "none";
  if (typeof value === "string") return value;
  if (typeof value === "boolean" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map((item) => (item == null ? "none" : String(item))).join(", ");
  return JSON.stringify(value);
}

function ConfigUsedPanel({ knobs }: { knobs: NonNullable<Run["resolved_knobs"]> }) {
  const entries = Object.entries(knobs.values).sort(([aName], [bName]) => {
    const aJob = knobs.sources[aName] === "job" ? 0 : 1;
    const bJob = knobs.sources[bName] === "job" ? 0 : 1;
    return aJob - bJob || aName.localeCompare(bName);
  });
  const jobCount = entries.filter(([name]) => knobs.sources[name] === "job").length;
  return (
    <Panel className="mb-3.5">
      <details className="group/details">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3.5 py-[9px] [&::-webkit-details-marker]:hidden">
          <span data-slot="panel-title" className="text-[12.5px] font-semibold whitespace-nowrap text-foreground">
            Config used
          </span>
          <span className="text-xs text-muted-foreground">
            {entries.length} knobs, {jobCount === 0 ? "all defaults" : `${jobCount} set by job`}
          </span>
          <ChevronRight size={15} aria-hidden="true" className="ml-auto shrink-0 text-muted-foreground transition-transform group-open/details:rotate-90" />
        </summary>
        <Panel.Body tight>
          <dl className="grid grid-cols-1 gap-x-6 px-3.5 pb-2 md:grid-cols-2 xl:grid-cols-3">
            {entries.map(([name, value]) => (
              <div key={name} className="flex min-h-6 items-baseline gap-3 border-t border-border py-0.5 text-[11.5px]">
                <dt className="min-w-0 flex-1 truncate font-mono text-muted-foreground" title={name}>
                  {name}
                </dt>
                <dd className="min-w-0 max-w-[60%] shrink-0 truncate text-right font-mono text-foreground" title={formatKnobValue(value)}>
                  {formatKnobValue(value)}
                </dd>
                {knobs.sources[name] === "job" && (
                  <dd className="shrink-0">
                    <Badge variant="outline-info">job</Badge>
                  </dd>
                )}
              </div>
            ))}
          </dl>
        </Panel.Body>
      </details>
    </Panel>
  );
}

// The merchant sweep runs as two Temporal activities (probe -> anchor); the tracker
// shows every stage with its status (done / running / pending / failed / skipped) and
// which one is current. ``stats.stage`` is the engine's live label; the run status
// closes out the stages on a terminal outcome.
const MERCHANT_STAGES: { key: string; label: string }[] = [
  { key: "probing", label: "Probe" },
  { key: "anchoring", label: "Anchor" },
];

type StageState = "done" | "running" | "pending" | "failed" | "skipped";
const STAGE_TONE: Record<StageState, string> = {
  done: "ok",
  running: "brand",
  failed: "crit",
  pending: "idle",
  skipped: "idle",
};
const STAGE_MARK: Record<StageState, string> = {
  done: " done",
  running: "",
  failed: " failed",
  pending: "",
  skipped: " - skipped",
};

function StageTracker({ stage, status, live }: { stage?: string; status: string; live: boolean }) {
  const curr = MERCHANT_STAGES.findIndex((s) => s.key === stage);
  const failed = status === "failed" || status === "timed_out" || status === "cancelled";
  const succeeded = status === "succeeded" || status === "degraded";
  const stateFor = (i: number): StageState => {
    if (succeeded) return "done";
    if (curr < 0) return "pending"; // not started a stage yet (queued / claimed)
    if (i < curr) return "done";
    if (i === curr) return failed ? "failed" : "running";
    return failed ? "skipped" : "pending"; // stages after the current one
  };
  return (
    <span
      className="row"
      style={{ gap: 6, alignItems: "center" }}
      title="Merchant sweep runs as two Temporal activities: probe (serviceability cascade) -> anchor (windowed anchor-gen + dimension compaction)"
    >
      {MERCHANT_STAGES.map((s, i) => {
        const st = stateFor(i);
        return (
          <span key={s.key} className="row" style={{ gap: 6, alignItems: "center" }}>
            {i > 0 && <ChevronRight size={11} className="faint" aria-hidden />}
            <Badge
              variant={toneVariant(STAGE_TONE[st] as Tone)}
              style={{ fontSize: 11, opacity: st === "pending" || st === "skipped" ? 0.55 : 1 }}
            >
              {st === "running" && live && <Dot live />}
              {s.label}
              {STAGE_MARK[st]}
            </Badge>
          </span>
        );
      })}
    </span>
  );
}

// Store-SKU coverage, the unit of work is one (dark store, SKU) check. A batch can
// split (partial responses), so we account per store-SKU pair: written / deduped /
// gave-up / not-serviceable / pending of store_sku_total.
// Per-fetch retry churn lives in ChurnPanel so the two aren't conflated.
// The SEARCH prepass that runs ahead of the PDP pass on a shelf-id run. It has its own
// panel rather than folding into the coverage bar because it answers a different question
// at a different price: the coverage bar counts store-SKU pairs the PDP pass resolved,
// while this counts stores swept and ids harvested for free off pages the search returned.
// Rendered only once the prepass has published, so a run without one shows nothing extra.
function SearchPrepassPanel({ stats, live }: { stats: CartProgress; live: boolean }) {
  const total = stats.search_stores_total ?? 0;
  const done = stats.search_stores_done ?? 0;
  const failed = stats.search_stores_failed ?? 0;
  const rows = stats.search_rows_written ?? 0;
  const sightings = stats.search_sightings ?? 0;
  const rot = stats.search_rotations ?? 0;
  const fails = stats.search_failures ?? 0;
  const cleared = stats.search_pairs_cleared;
  const pending = total - done - failed;
  const denom = Math.max(1, total);
  const pct = (n: number) => `${((n / denom) * 100).toFixed(1)}%`;
  // Ids per lease is the number that says whether searching is actually cheaper than the
  // PDP pass it front-runs; the PDP pass's own equivalent is roughly 5 at high concurrency.
  const perLease = rot > 0 ? rows / rot : null;
  // What the prepass cost against what the PDP pass cost. The lease and row counters say
  // how much was harvested; only bytes say whether harvesting it that way was cheaper,
  // which is the entire bet the prepass makes.
  const searchDown = stats.search_bytes_down;
  const pdpDown = stats.pdp_bytes_down;
  return (
    <Panel style={{ gridColumn: "span 5" }}>
      <Panel.Head title="Search prepass">
        <div className="flex-1" />
        {perLease != null && (
          <Badge variant="outline-success" style={{ fontSize: 10.5 }} title="ids written / proxy leases spent">
            {perLease.toFixed(0)} ids / lease
          </Badge>
        )}
        <span className="num faint" style={{ fontSize: 11, marginLeft: 8 }}>
          {fmtInt(total)} stores
        </span>
      </Panel.Head>
      <Panel.Body>
        <div className="stack" style={{ height: 12, marginBottom: 12 }}>
          <i className="ok" style={{ width: pct(done) }} />
          <i className="crit" style={{ width: pct(failed) }} />
          <i className="idle" style={{ width: pct(pending) }} />
        </div>
        <table className="tbl">
          <tbody>
            <tr><td><Dot tone="ok" /> searched</td><td className="num">{fmtInt(done)}</td><td className="num faint">{pct(done)}</td></tr>
            <tr><td><Dot tone="crit" /> gave up</td><td className="num">{fmtInt(failed)}</td><td className="num faint">{pct(failed)}</td></tr>
            <tr><td><Dot tone="idle" /> pending</td><td className="num">{fmtInt(pending)}</td><td className="num faint">{pct(pending)}</td></tr>
          </tbody>
        </table>
        {cleared != null && (
          <div className="row" style={{ gap: 8, alignItems: "baseline", marginBottom: 8 }}>
            <span className="num" style={{ fontSize: 20 }}>{fmtInt(cleared)}</span>
            <span className="hint">
              pairs cleared from the PDP worklist: what the prepass actually saved
            </span>
          </div>
        )}
        {searchDown != null && (
          <table className="tbl">
            <tbody>
              <tr>
                <td>search bandwidth</td>
                <td className="num">{fmtBytes(searchDown)}</td>
                <td className="num faint">{fmtBytes(stats.search_bytes_up)} up</td>
              </tr>
              {pdpDown != null && (
                <tr>
                  <td>PDP pass, same run</td>
                  <td className="num">{fmtBytes(pdpDown)}</td>
                  <td className="num faint">{fmtBytes(stats.pdp_bytes_up)} up</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        <div className="hint" style={{ marginTop: 10 }}>
          <strong>{fmtInt(rows)}</strong> ids written from <strong>{fmtInt(sightings)}</strong> cards
          {live ? " so far" : ""}, over {fmtInt(rot)} proxy leases ({fmtInt(fails)} failed).
          A search card states the store-local id for every product on the page, so these
          cost no PDP at all, the pairs they answer are simply absent from the worklist the
          PDP pass is then costed against. A store this pass never reached is not lost: its
          pairs stay in that worklist and are answered the expensive way, as before.
          Ids written far exceeds pairs cleared by design: a brand search returns that
          brand&rsquo;s whole catalogue, most of it SKUs this dimension does not track, plus
          pairs something already answered. Those rows cost nothing extra and keep, but only
          the cleared count shrinks this run.
        </div>
      </Panel.Body>
    </Panel>
  );
}

export function CoveragePanel({
  stats,
  title = "SKU x dark-store coverage",
}: {
  stats: CartProgress;
  title?: string;
}) {
  const total = stats.store_sku_total ?? 0;
  const written = stats.rows_emitted ?? 0;
  const giveup = stats.store_sku_giveup ?? 0;
  const unresolved = stats.store_sku_unresolved_id ?? 0;
  const notServ = stats.store_sku_not_serviceable ?? 0; // store never resolved a merchant
  const delisted = stats.store_sku_delisted ?? 0; // not sold at a serviceable store
  const savedAbsence = stats.store_sku_delisted_saved_absence;
  const pdpAbsence = stats.store_sku_delisted_pdp_absence;
  // Older runs have neither source counter. During a retry, the coverage total can
  // briefly lead the source counters, so show the split only when it reconciles.
  const showDelistedSplit = savedAbsence !== undefined && pdpAbsence !== undefined
    && savedAbsence + pdpAbsence === delisted && delisted > 0;
  const deduped = stats.rows_deduped ?? 0; // already written at a coincident effective store
  // Raw, unclamped: written + giveup + unresolved + notServ + delisted + deduped + pending == total is an
  // invariant the backend retry-merge preserves (give-ups are take-current, coverage is
  // max-floored, so no pair is ever counted in two buckets). A NEGATIVE pending would mean a
  // bucket double-counted -- surface it loudly rather than clamp it to 0 and hide the bug.
  const pending = total - written - giveup - unresolved - notServ - delisted - deduped;
  const denom = Math.max(1, total);
  const pct = (n: number) => `${((n / denom) * 100).toFixed(1)}%`;
  const answerable = written + giveup + unresolved; // delisted/not-serv/pending aren't "answerable"
  const yieldPct = answerable > 0 ? (written / answerable) * 100 : null;
  const yieldTone = yieldPct == null ? "idle" : yieldPct >= 50 ? "ok" : yieldPct >= 20 ? "warn" : "crit";
  const rows: { label: string; tone: string; n: number }[] = [
    { label: "written", tone: "ok", n: written },
    ...(deduped > 0 ? [{ label: "deduped (covered elsewhere)", tone: "info", n: deduped }] : []),
    ...(delisted > 0 ? [{ label: "delisted (not sold here)", tone: "info", n: delisted }] : []),
    ...(notServ > 0 ? [{ label: "not serviceable", tone: "warn", n: notServ }] : []),
    ...(unresolved > 0 ? [{ label: "unresolved shelf ID", tone: "warn", n: unresolved }] : []),
    { label: "gave up", tone: "crit", n: giveup },
    { label: "pending", tone: "idle", n: pending },
  ];
  return (
    <Panel style={{ gridColumn: "span 5" }}>
      <Panel.Head title={title}>
        <div className="flex-1" />
        {yieldPct != null && (
          <Badge variant={toneVariant(yieldTone as Tone)} style={{ fontSize: 10.5 }} title="written / (written + gave up + unresolved shelf ID)">
            {yieldPct.toFixed(0)}% yield
          </Badge>
        )}
        <span className="num faint" style={{ fontSize: 11, marginLeft: 8 }}>{fmtInt(total)} store-SKU pairs</span>
      </Panel.Head>
      <Panel.Body>
        <div className="stack" style={{ height: 12, marginBottom: 12 }}>
          <i className="ok" style={{ width: pct(written) }} />
          <i className="info" style={{ width: pct(deduped) }} />
          <i className="info" style={{ width: pct(delisted) }} />
          <i className="warn" style={{ width: pct(notServ) }} />
          <i className="warn" style={{ width: pct(unresolved) }} />
          <i className="crit" style={{ width: pct(giveup) }} />
          <i className="idle" style={{ width: pct(pending) }} />
        </div>
        <table className="tbl">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} />
                    <span className="inline-flex items-center gap-1.5">
                      {r.label}
                      {r.label === "delisted (not sold here)" && showDelistedSplit && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label="Delisted source breakdown"
                                className="inline-flex size-4 items-center justify-center rounded-full text-ink-3 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                              >
                                <Info size={14} aria-hidden="true" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" align="start" className="min-w-56 p-3">
                              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                                Of {fmtInt(delisted)} delisted pairs
                              </div>
                              <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 font-normal">
                                <dt>Saved confirmed absence</dt>
                                <dd className="num text-right font-semibold">{fmtInt(savedAbsence)}</dd>
                                <dt>Fresh PDP-confirmed absence</dt>
                                <dd className="num text-right font-semibold">{fmtInt(pdpAbsence)}</dd>
                              </dl>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </span>
                  </span>
                </td>
                <td className="r num strong">{fmtInt(r.n)}</td>
                <td className="r faint num">{pct(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          Each store-SKU check is written, deduped, delisted, not serviceable, unresolved, or gave up.
          Unresolved means no inventory verdict: the shelf ID may be unknown, or a PDP recheck may be inconclusive.
          Per-fetch retries are tracked separately, in the churn panel.
        </p>
      </Panel.Body>
    </Panel>
  );
}

function RankCoveragePanel({
  stats,
  shelfNoun,
  coveragePct,
  thresholdPct,
}: {
  stats: CartProgress;
  shelfNoun: string;
  coveragePct?: number;
  thresholdPct?: number;
}) {
  const total = stats.probes_planned ?? 0;
  const ok = stats.probes_ok ?? 0;
  const empty = stats.probes_no_results ?? 0;
  const unknown = stats.probes_unknown_category ?? 0;
  const failed = stats.probes_failed ?? 0;
  // Reported beside the title, never as a segment of the bar. The four outcome counters
  // are SUMMED across a run's Temporal attempts (`_RANK_SUM_KEYS`) while this one is
  // floored, so a probe inherited from attempt 1 is already inside `ok`/`empty`/
  // `unknown`/`failed` -- a "resumed" slice on top of them makes the parts add up to
  // `planned + resumed` and pushes the bar past 100%.
  const resumed = stats.probes_resumed ?? 0;
  // Raw, unclamped, the same rule the cart coverage panel follows: a negative pending is a
  // double-count worth seeing rather than a zero worth trusting.
  //
  // Taken from `probes_done`, which counts a RESUMED probe as well as one this leg
  // answered. Subtracting the outcome counters instead reports every probe inherited from
  // an earlier attempt as still to do, so a run that resumed the lot sits at "pending"
  // forever with nothing left to ask.
  const pending = total - (stats.probes_done ?? 0);
  const denom = Math.max(1, total);
  const pct = (n: number) => `${((n / denom) * 100).toFixed(1)}%`;
  // Coverage is the run's own verdict and only exists once it finished. Below the job's
  // threshold this run's shares cannot be compared with the previous run's, which is the
  // whole point of measuring, so the badge goes crit rather than merely warn.
  const short = coveragePct != null && thresholdPct != null && coveragePct < thresholdPct;
  const rows: { label: string; tone: string; n: number }[] = [
    { label: "measured", tone: "ok", n: ok },
    { label: "nothing there", tone: "info", n: empty },
    ...(unknown > 0 ? [{ label: `${shelfNoun} that no longer resolve`, tone: "warn", n: unknown }] : []),
    { label: "never answered", tone: "crit", n: failed },
    { label: "pending", tone: "idle", n: pending },
  ];
  return (
    <Panel style={{ gridColumn: "span 5" }}>
      <Panel.Head title="Shelf coverage">
        <div className="flex-1" />
        {/* A run whose plan resolved empty publishes 100% coverage -- the coverage of
            nothing is whole -- and it finalizes DEGRADED under a reason that says so. The
            badge is hidden there rather than printed green over a table of zeros. */}
        {coveragePct != null && total > 0 && (
          <Badge
            variant={toneVariant((short ? "crit" : "ok") as Tone)}
            style={{ fontSize: 10.5 }}
            title={
              thresholdPct == null
                ? "probes answered / probes planned"
                : `answered / planned. Below the job's ${thresholdPct}% threshold this run's shares are not comparable with the previous run's.`
            }
          >
            {coveragePct}% coverage
          </Badge>
        )}
        {resumed > 0 && (
          <Badge
            variant="outline-info"
            style={{ fontSize: 10.5, marginLeft: 8 }}
            title="Answered by an earlier attempt of this run and never re-asked. Already counted in the outcomes below, so it is not a slice of its own."
          >
            {fmtInt(resumed)} resumed
          </Badge>
        )}
        <span className="num faint" style={{ fontSize: 11, marginLeft: 8 }}>{fmtInt(total)} probes</span>
      </Panel.Head>
      <Panel.Body>
        <div className="stack" style={{ height: 12, marginBottom: 12 }}>
          <i className="ok" style={{ width: pct(ok) }} />
          <i className="info" style={{ width: pct(empty) }} />
          <i className="warn" style={{ width: pct(unknown) }} />
          <i className="crit" style={{ width: pct(failed) }} />
          <i className="idle" style={{ width: pct(pending) }} />
        </div>
        <table className="tbl">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
                  </span>
                </td>
                <td className="r num strong">{fmtInt(r.n)}</td>
                <td className="r faint num">{pct(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          One probe = one shelf asked at one dark store. A shelf the platform has nothing
          for is an answer and counts as measured coverage; only a shelf we never got to is
          a gap. Per-ask retries are tracked separately, in the churn panel.
        </p>
      </Panel.Body>
    </Panel>
  );
}

// -- pipeline observability ---------------------------------------------------
// Rate + latency for the two ends of the pipeline, which the churn panel next to it
// cannot show: churn counts CUMULATIVE outcomes, and a cumulative count answers "what
// happened over the whole run", never "what is happening now". A run that has been
// blocked for the last four minutes looks identical to one that was blocked in its first
// four, until you watch the rate.
//
// Two groups, deliberately not merged. The CRAWLER is what we asked of the flow: batch
// attempts, how many came back usable, and what the retries cost. The TARGET is what the
// platform's domain served: HTTP requests (a different quantity - one attempt can spend
// several, and a prime spends one with no attempt behind it), how it answered, and how
// long it took. Reading a request rate off batch counts is how a fan-out platform gets
// mistaken for a slow one.

// Live rate of a set of cumulative counters, measured over a trailing window.
//
// Sampled on RENDER, which is what makes it honest: the run page re-renders at 1Hz
// whether or not new progress arrived, so a stalled run's rate decays toward zero instead
// of freezing at its last healthy value. Returns null until the window holds two samples
// spanning MIN_SPAN_MS - a rate off a 300ms gap is noise, and showing it would have the
// panel flicker between wild numbers.
const RATE_WINDOW_MS = 45_000;
const RATE_MIN_SPAN_MS = 6_000;

type LiveWindow = { rate: Record<string, number | null>; delta: Record<string, number | null> };

/** Per-second rates AND the raw counter deltas over the trailing window.
 *
 *  Both, because a RATIO of two counters (calls per batch, calls per row) must be built
 *  from the same window as the rates beside it. A ratio of the cumulative totals is a
 *  run-lifetime average: it is dragged by everything that has already happened, so a
 *  fan-out that doubles in the last minute barely moves it and the panel keeps reporting
 *  a number that stopped being true. Dividing this window's numerator by this window's
 *  denominator tracks what the run is doing NOW, which is the whole point of a live panel. */
function useLiveRates(values: Record<string, number>, live: boolean): LiveWindow {
  const samples = useRef<{ t: number; v: Record<string, number> }[]>([]);
  if (!live) samples.current = [];
  if (live) {
    const now = Date.now();
    samples.current = [...samples.current.filter((s) => now - s.t <= RATE_WINDOW_MS), { t: now, v: values }];
  }
  const first = samples.current[0];
  const last = samples.current[samples.current.length - 1];
  const span = first && last ? (last.t - first.t) / 1000 : 0;
  const usable = first != null && last != null && last.t - first.t >= RATE_MIN_SPAN_MS;
  const rate: Record<string, number | null> = {};
  const delta: Record<string, number | null> = {};
  for (const k of Object.keys(values)) {
    const moved = usable ? Math.max(0, (last.v[k] ?? 0) - (first.v[k] ?? 0)) : null;
    delta[k] = moved;
    rate[k] = moved == null ? null : moved / span;
  }
  return { rate, delta };
}

// Tone per latency class, so the split reads at a glance: what we wanted (served), what
// the target refused (refused), and what never worked (failed / denied).
const LATENCY_CLASS_TONE: Record<string, string> = {
  served: "ok",
  refused: "warn",
  shed: "idle",
  failed: "crit",
  denied: "crit",
};

type MetricRow = {
  label: string;
  // The technical name, when the label is the plain-English one. Rendered muted beside it
  // so the row teaches the term instead of replacing it.
  tag?: string;
  tone: string;
  value: string;
  // A second measured column, for a group that reports two readings of the same thing
  // (p50 AND p95 per outcome class). Set `cols` on the group to name them.
  value2?: string;
  foot?: string;
  // The two sides of the ratio the value came from, each carrying its own UNIT. A bare
  // "180 / 383" says nothing a reader can check; "180 retried / 383 tried" is a sentence,
  // and it is what lets two rows be compared (same denominator) or not (different one).
  frac?: [n: number | undefined, nUnit: string, d: number | undefined, dUnit: string];
  // "/" reads as a division and "|" as a split. "26 calls / 20 rows" IS a division;
  // "192 faster | 192 slower" is one population cut in two, and a slash there invites the
  // reader to divide two numbers that were never meant to be divided.
  sep?: string;
  hint?: string;
};

// The third column: the fraction when the row has one, else the plain foot.
function RowTail({ row }: { row: MetricRow }) {
  if (!row.frac) return <>{row.foot ?? ""}</>;
  const [n, nu, d, du] = row.frac;
  return (
    <>
      {fmtInt(n)} <u>{nu}</u> <s>{row.sep ?? "/"}</s> {fmtInt(d)} <u>{du}</u>
    </>
  );
}

function MetricGroup({
  label,
  hint,
  rows,
  cols,
}: {
  label: string;
  hint: string;
  rows: MetricRow[];
  // Names the two value columns. With them the group reads as a table - one row per
  // subject, one column per reading - instead of one number with the other buried in a
  // foot, where the two cannot be compared down the column.
  cols?: [string, string];
}) {
  return (
    <>
      <tr style={{ borderTop: "1px solid var(--line)" }}>
        <td colSpan={cols ? 1 : 3} style={{ paddingTop: 9, paddingBottom: 3 }} title={hint}>
          <span className="eyebrow" style={{ fontSize: 9.5 }}>{label}</span>
        </td>
        {cols && (
          <>
            <td className="r" style={{ paddingTop: 9, paddingBottom: 3 }}>
              <span className="eyebrow" style={{ fontSize: 9.5 }}>{cols[0]}</span>
            </td>
            <td className="r" style={{ paddingTop: 9, paddingBottom: 3 }}>
              <span className="eyebrow" style={{ fontSize: 9.5 }}>{cols[1]}</span>
            </td>
          </>
        )}
      </tr>
      {rows.map((r) => (
        <tr key={r.label}>
          <td title={r.hint}>
            <span className="row">
              <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
              {r.tag && (
                <span className="faint num" style={{ fontSize: 9.5, marginLeft: 5 }}>
                  {r.tag}
                </span>
              )}
            </span>
          </td>
          <td className="r num strong">{r.value}</td>
          {r.value2 != null ? (
            <td className="r num strong">{r.value2}</td>
          ) : (
            <td className="r faint num pp-frac" style={{ fontSize: 10.5 }}>
              <RowTail row={r} />
            </td>
          )}
        </tr>
      ))}
    </>
  );
}

// States a block's denominator ONCE, above its rows, instead of repeating it in every
// tooltip below them. This is the whole fix: the panel's recurring question has always
// been "percent of what", and the answer was only ever available on hover.
function DenomBanner({ n, unit, note }: { n: number; unit: string; note?: ReactNode }) {
  return (
    <div className="pp-denom">
      <span className="k">out of</span>
      <span className="d">{fmtInt(n)}</span>
      <span className="t">
        <span>{unit}</span>
        {note != null && <> &middot; {note}</>}
      </span>
    </div>
  );
}

// An exhaustive split as one bar. Four percentage rows make the reader add them up to
// check nothing is missing; a bar shows the composition, that it closes, and which slice
// dominates, in one look. Slices are already filtered to the non-empty ones.
function CompositionBar({ slices, total }: { slices: Slice[]; total: number }) {
  if (slices.length === 0 || total <= 0) return null;
  return (
    <div style={{ padding: "12px 14px 14px" }}>
      <div className="pp-bar">
        {slices.map((sl) => (
          <i key={sl.key} style={{ flex: sl.n, background: dotColor(sl.tone) }} title={`${sl.label}: ${fmtInt(sl.n)}`}>
            {sl.n / total >= 0.12 ? fmtPct((sl.n / total) * 100) : ""}
          </i>
        ))}
      </div>
      <div className="pp-legend">
        {slices.map((sl) => (
          <span key={sl.key}>
            <b style={{ color: dotColor(sl.tone) }}>{fmtInt(sl.n)}</b> {sl.label}
          </span>
        ))}
      </div>
    </div>
  );
}




// Exported for the render test alongside this file; the run page is its only caller.
export function PipelinePanel({
  stats,
  elapsed,
  live,
}: {
  stats: CartProgress;
  elapsed: number | null;
  live: boolean;
}) {
  const attempts = crawlerAttempts(stats);
  const succeeded = crawlerSucceeded(stats);
  const requests = stats.http_requests;
  const win = useLiveRates(
    {
      attempts,
      succeeded,
      requeued: stats.batches_requeued ?? 0,
      requests: requests ?? 0,
      rows: stats.rows_emitted ?? 0,
    },
    live,
  );
  const rates = win.rate;
  // A ratio over the SAME trailing window as the rates, falling back to the run totals
  // when the window has not moved yet (or the run is done, where the lifetime average IS
  // the honest answer). Numerator and denominator are kept apart and divided at the end -
  // averaging a ratio would weight a quiet second the same as a busy one.
  const windowRatio = (num: string, den: string): number | null => {
    const n = win.delta[num];
    const d = win.delta[den];
    return n != null && d != null && d > 0 ? n / d : null;
  };
  // The request axis exists only on runs recorded since the counters landed. Older runs
  // still get the crawler half rather than an empty panel - the batch counters they DO
  // carry are enough for every rate on that side.
  const hasRequests = requests != null;
  const hasLatency = latencySamples(stats) > 0;
  // Fan-out: the calls one batch's own WORK costs. Session setup is netted out - it is a
  // per-lease cost the lease amortises over every batch it serves, so charging it to a
  // batch would report a fan-out no batch has. It is not hidden: the row below counts it.
  // 1.0 means one call per attempt; above that the adapter is spending several, which is
  // the difference between the rate we think we're sending and the one the target sees.
  // Fan-out counts EVERY call an attempt spent, session setup included. Netting setup out
  // made the ratio smaller than the footprint the target's limiter actually sees, and made
  // the difference invisible - the reader had no way to know a number had been adjusted.
  // The composition bar carries that split instead, where it is a visible slice.
  const fanout = hasRequests
    ? (windowRatio("requests", "attempts") ?? (attempts > 0 ? requests / attempts : null))
    : null;
  const firstTry = firstTryRate(stats);
  const retryWorks = retryEffectiveness(stats);
  const perCall = windowRatio("rows", "requests") ?? rowsPerRequest(stats);
  // The shape behind the first-try rate. A mean cannot separate "nearly all take one, a
  // tail takes twenty" from "everything takes three", and those want different fixes.
  const attemptRows: MetricRow[] = ANSWER_ATTEMPT_BUCKETS.filter(
    (b) => (stats.answers_by_attempts?.[b] ?? 0) > 0,
  ).map((bucket) => {
    const n = stats.answers_by_attempts?.[bucket] ?? 0;
    return {
      label: bucket === "1" ? "1 try" : `${bucket} tries`,
      tone: bucket === "1" ? "ok" : bucket === "2" ? "info" : bucket === "11+" ? "crit" : "warn",
      value: fmtInt(n),
      foot: fmtPct((n / Math.max(1, answersTotal(stats))) * 100),
      hint: "Pairs the platform answered after this many attempts.",
    };
  });
  const retryPct = attemptShare(stats, stats.batches_requeued);
  const answers = answersTotal(stats);
  const recovered = answers - (stats.answers_by_attempts?.["1"] ?? 0);

  // -- block 1: the work queue. Denominator: one batch attempt. --------------
  const batchRows: MetricRow[] = [
    {
      label: "needed a retry",
      tone: retryPct == null ? "idle" : retryPct >= 100 ? "crit" : retryPct >= 40 ? "warn" : "ok",
      value: fmtPct(retryPct),
      frac: [stats.batches_requeued, "retried", attempts, "tried"],
      hint: "Requeues per attempt: a failed attempt handed back to the queue for a fresh proxy. Can exceed 100%, and the fraction shows why - one batch that fails five times books five requeues against five attempts.",
    },
    ...(answers > 0
      ? [
          {
            label: "retries that worked",
            tone: retryWorks == null ? "idle" : retryWorks >= 60 ? "ok" : retryWorks >= 25 ? "warn" : "crit",
            value: fmtPct(retryWorks),
            frac: [recovered, "saved", recovered + (stats.store_sku_giveup ?? 0), "retried"],
            hint: "Of the store-SKU pairs that needed a retry and then ended, the share the retries actually recovered - the rest gave up. Near zero means the attempt budget is being spent on pairs that were never going to answer.",
          } satisfies MetricRow,
          {
            label: "answered first try",
            tone: firstTry == null ? "idle" : firstTry >= 80 ? "ok" : firstTry >= 50 ? "warn" : "crit",
            value: fmtPct(firstTry),
            frac: [stats.answers_by_attempts?.["1"], "pairs", answers, "answered"],
            hint: "Pairs, not batches: one good batch rescues a hundred pairs at once, so this can be high on a run where barely half the attempts worked. A harder question than goodput, which only asks whether an attempt was usable.",
          } satisfies MetricRow,
        ]
      : []),
    {
      label: "timed out",
      tone: (stats.batches_timeout ?? 0) > 0 ? "warn" : "ok",
      value: fmtPct(attemptShare(stats, stats.batches_timeout)),
      frac: [stats.batches_timeout, "timeouts", attempts, "tried"],
      hint: "Attempts whose request never came back inside the flow's timeout. Charged to the proxy, and each one holds a worker slot for the full timeout.",
    },
  ];

  // -- block 2: the wire. Denominator: one HTTP call. ------------------------
  const callRows: MetricRow[] = hasRequests
    ? [
        {
          label: "server errors",
          tone: (stats.http_5xx ?? 0) > 0 ? "warn" : "ok",
          value: fmtPct(requests > 0 ? ((stats.http_5xx ?? 0) / requests) * 100 : null),
          frac: [stats.http_5xx, "5xx", requests, "calls"],
          hint: "Origin errors. They have no churn row of their own: the cart ladder folds a 5xx into transport_error so the IP rotates, where it reads as a bad tunnel.",
        },
        {
          label: "rows per call",
          tone: "idle",
          value: perCall == null ? "-" : perCall.toFixed(2),
          frac: [stats.rows_emitted, "rows", requests, "calls"],
          hint: "What one call to the target buys, whole bill included - session setup and the calls spent on batches that were then refused. Above 1.0 is normal and good: one cart call can answer for twenty ids at once. FALLING across runs is a platform getting more expensive, visible here before the proxy bill shows it.",
        },
      ]
    : [];

  // -- block 3: the clock. Denominator: one timed attempt. -------------------
  const timed = latencySamples(stats);
  const latencyRows: MetricRow[] = hasLatency
    ? [
        {
          label: "typical attempt",
          sep: "|",
          tag: "p50",
          tone: "ok",
          value: fmtLatencyPercentile(stats, 50),
          frac: [Math.floor(timed / 2), "faster", timed - Math.floor(timed / 2), "slower"],
          hint: "The middle timing: half the run was faster than this, half slower. It is what a run feels like. One stopwatch per ATTEMPT, covering however many calls it spent - divide by calls per batch for a per-call figure.",
        },
        {
          label: "a bad one",
          sep: "|",
          tag: "p95",
          tone: "warn",
          value: fmtLatencyPercentile(stats, 95),
          frac: [timed - Math.ceil(timed * 0.05), "faster", Math.ceil(timed * 0.05), "slower"],
          hint: "Only one attempt in twenty was slower. This is the number that sets your timeouts and decides how long a stuck worker holds a slot; an average hides it completely. The GAP between p50 and p95 is the finding - a small gap is uniformly slow, a large one is mostly-fast-with-stragglers.",
        },
        {
          label: "worst single attempt",
          sep: "|",
          tone: "idle",
          value: fmtMs(stats.latency_ms_max),
          frac: [1, "attempt", timed, "timed"],
          hint: "The slowest timing in the run, off an exact running maximum rather than the histogram. Read it beside the mean: a big gap between them means one or two attempts, not a pattern.",
        },
      ]
    : [];
  // Latency split by what the request got back. The blended p50 above cannot tell a target
  // that is THROTTLING (slow 200s) from one that is REFUSING (fast 403s), and they call for
  // opposite responses - back off, or rotate. A class the run never saw renders no row.
  const classRows: MetricRow[] = latencyClasses(stats).map((cls) => {
    const n = latencySamples(stats, cls);
    return {
      label: cls,
      tone: LATENCY_CLASS_TONE[cls] ?? "idle",
      value: fmtLatencyPercentile(stats, 50, cls),
      value2: fmtLatencyPercentile(stats, 95, cls),
      hint: `${LATENCY_CLASS_HELP[cls] ?? ""} ${fmtInt(n)} attempts (${fmtPct((n / Math.max(1, latencySamples(stats))) * 100)} of them), mean ${fmtMs(latencyMean(stats, cls))}.`,
    };
  });
  // The exit-node shape: the one question a block rate cannot answer. 12 burned nodes out
  // of 200 is a rotation problem; 200 of 200 is the target refusing everyone, and no
  // amount of rotating will fix it.
  const nodes = stats.proxy_ips_used;
  const burned = stats.proxy_ips_burned ?? 0;
  const degraded = stats.proxy_ips_degraded ?? 0;
  const clean = stats.proxy_ips_clean ?? 0;
  // The share of the roster taking real refusals, which is what decides whether rotating
  // is even the right lever. A handful of hot nodes is a rotation problem and a fresh IP
  // clears it; most of the roster refused alike is the TARGET refusing everyone, and the
  // next IP meets the same wall. The panel says which, rather than leaving the reader to
  // divide two numbers and know the rule.
  const hitPct = nodes && nodes > 0 ? ((degraded + burned) / nodes) * 100 : null;
  const headline = rates.requests ?? rates.attempts ?? perSecond(requests ?? attempts, elapsed);
  return (
    <Panel>
      <Panel.Head title="Pipeline rate">
        <div className="flex-1" />
        {headline != null && (
          <Badge
            variant="outline-info"
            style={{ fontSize: 10.5 }}
            title={live ? "Trailing 45s window, sampled live." : "Run average over its full wall time."}
          >
            {live && <Dot live />} {fmtRate(headline)}
          </Badge>
        )}
      </Panel.Head>
      <Panel.Body>
        {/* -- block 1: every batch we tried. Cart and brand both report ``batches_*``
            churn; merchant counts grid cells and has its own panel for them, so this block
            simply does not render there. The wire blocks below are the shared part. -- */}
        {attempts > 0 && (
          <>
        <DenomBanner
          n={attempts}
          unit="batch attempts"
          note={
            <>
              <span className="num">{fmtRate(rates.attempts ?? perSecond(attempts, elapsed))}</span> &middot; retries
              included
            </>
          }
        />
        <CompositionBar slices={attemptComposition(stats)} total={attempts} />
        <table className="tbl">
          <tbody>
            <MetricGroup
              label="of those, how many"
              hint="Every row below is a share of the batch attempts named in the banner above."
              rows={batchRows}
            />
          </tbody>
        </table>

          </>
        )}

        {/* -- block 2: every call we sent -- */}
        {hasRequests && (
          <>
            <DenomBanner
              n={requests}
              unit="HTTP calls"
              note={
                <>
                  <span className="num">{fmtRate(rates.requests ?? perSecond(requests, elapsed))}</span> &middot;{" "}
                  <b className="num">{fanout == null ? "?" : fanout.toFixed(1)} per batch</b>
                </>
              }
            />
            <CompositionBar slices={callComposition(stats)} total={requests} />
            <table className="tbl">
              <tbody>
                <MetricGroup
                  label="of those, how many"
                  hint="Every row below is a share of the HTTP calls named in the banner above - a different denominator from the block before it."
                  rows={callRows}
                />
              </tbody>
            </table>
          </>
        )}

        {/* -- block 3: how long an attempt took -- */}
        {hasLatency && (
          <>
            <DenomBanner
              n={timed}
              unit="timed attempts"
              note={
                <>
                  each clock covers that attempt&rsquo;s whole{" "}
                  <b className="num">{fanout == null ? "" : `${fanout.toFixed(1)}-call`}</b> sequence, not one call
                </>
              }
            />
            <table className="tbl">
              <tbody>
                <MetricGroup
                  label="of those timings"
                  hint="One stopwatch per attempt. On a platform with fan-out this covers several calls, so it is not a per-request latency."
                  rows={latencyRows}
                />
                {classRows.length > 0 && (
                  <MetricGroup
                    label="split by what came back"
                    cols={["p50", "p95"]}
                    hint="A slow 200 is a target throttling us; a fast 403 is a target refusing us. One blended latency is a middle number that describes neither. Both percentiles per class, so a class that is typically fine but has a bad tail cannot hide behind its median."
                    rows={classRows}
                  />
                )}
              </tbody>
            </table>
          </>
        )}

        {/* -- block 4: the exit nodes -- */}
        {nodes != null && nodes > 0 && (
          <>
            <DenomBanner
              n={nodes}
              unit="distinct exit IPs"
              note={
                <>
                  <span className="num">{fmtInt(stats.rotations)}</span> rotations &middot; scored on this run only
                </>
              }
            />
            <CompositionBar
              slices={[
                { key: "clean", label: PROXY_REFUSAL_BANDS.clean, tone: "ok", n: clean },
                { key: "degraded", label: PROXY_REFUSAL_BANDS.degraded, tone: "warn", n: degraded },
                { key: "burned", label: PROXY_REFUSAL_BANDS.burned, tone: "crit", n: burned },
              ].filter((sl) => sl.n > 0)}
              total={nodes}
            />
            <table className="tbl">
              <tbody>
                <MetricGroup
                  label="of those nodes"
                  hint="Judged on THIS run's attempts only; the pool keeps its own longer-lived scores and owns whether a node is actually evicted."
                  rows={[
                    {
                      label: "hitting refusals",
                      tone: hitPct == null ? "idle" : hitPct >= 35 ? "crit" : hitPct >= 10 ? "warn" : "ok",
                      value: fmtPct(hitPct),
                      frac: [degraded + burned, "refusing", nodes, "used"],
                      hint: "Nodes refused on 20% or more of their attempts - the amber and red bands above, added together. A node with fewer than four attempts is never judged: one 403 on a node's only attempt is a 100% refusal rate and no evidence at all.",
                    },
                  ]}
                />
              </tbody>
            </table>
          </>
        )}

        {/* -- the pair economics, which count a fifth thing -- */}
        {attemptRows.length > 0 && (
          <>
            <DenomBanner n={answers} unit="answered store-SKU pairs" note="pairs, not batches" />
            <table className="tbl">
              <tbody>
                <MetricGroup
                  label="how many tries each took"
                  hint="What a store-SKU answer actually cost. Pairs restored from the resume cache are absent: they cost no attempt at all, which is the point of the cache."
                  rows={attemptRows}
                />
              </tbody>
            </table>
          </>
        )}

        {!hasRequests && attempts > 0 && (
          <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
            This run predates the per-request counters, so the call block, the fan-out and the
            latency rows are absent. The batch block is derived from its batch counters.
          </p>
        )}
        <p className="faint" style={{ fontSize: 10.5, marginTop: 10, marginBottom: 0, lineHeight: 1.5 }}>
          Rates and the two ratios are the live trailing {RATE_WINDOW_MS / 1000}s window while the
          run is moving, and the run average once it is done. Churn on the right counts the same
          events cumulatively.
        </p>
      </Panel.Body>
    </Panel>
  );
}

// Taxonomy classify, the unit of work is one SKU classified by the LLM into the
// canonical tree. Each SKU lands in exactly one terminal bucket: classified (mapped to a
// leaf) / unmapped (legitimately no-fit) / unknown (model omitted it after a retry, left
// unlabelled for the next run) / pending (not yet reached this run).
// Matching has no proxies, stores or requests to report: its shape is listings in, SKUs
// out, and how much of that the model had to settle rather than the score.
function ProductMatchRunPanel({ stats }: { stats: CartProgress }) {
  const listings = stats.listings ?? 0;
  const groups = stats.groups ?? 0;
  const cross = stats.cross_platform ?? 0;
  // Raw, unclamped (see the cart CoveragePanel): a negative single count would mean
  // cross exceeded the group total, which is a double-count worth seeing.
  const single = groups - cross;
  const denom = Math.max(1, groups);
  const pct = (n: number) => `${((n / denom) * 100).toFixed(1)}%`;
  const rows: { label: string; tone: string; n: number }[] = [
    { label: "on 2+ platforms (comparable)", tone: "ok", n: cross },
    { label: "single-platform", tone: "idle", n: single },
  ];
  return (
    <Panel style={{ gridColumn: "span 6" }}>
      <Panel.Head title="Match outcome">
        <div className="flex-1" />
        <span className="num faint" style={{ fontSize: 11 }}>{fmtInt(groups)} SKUs</span>
      </Panel.Head>
      <Panel.Body>
        <div className="stack" style={{ height: 12, marginBottom: 12 }}>
          <i className="ok" style={{ width: pct(cross) }} />
          <i className="idle" style={{ width: pct(single) }} />
        </div>
        <table className="tbl">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
                  </span>
                </td>
                <td className="r num strong">{fmtInt(r.n)}</td>
                <td className="r faint num">{pct(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          {fmtInt(listings)} listings across {fmtInt(stats.brands)} brands;{" "}
          {fmtInt(stats.judged)} pairs went to the model (the rest were settled on name
          similarity alone)
          {(stats.combos_excluded ?? 0) > 0
            ? `; ${fmtInt(stats.combos_excluded)} combos held out as bundles of different products`
            : ""}
          . A single-platform SKU is usually a real catalogue difference, not a miss.
          Corrections live on the brand page under Matched SKUs.
        </p>
      </Panel.Body>
    </Panel>
  );
}

function TaxonomyRunPanel({ stats, live }: { stats: CartProgress; live: boolean }) {
  const total = stats.total ?? 0;
  const classified = stats.classified ?? 0;
  const unmapped = stats.unmapped ?? 0;
  const unknown = stats.unknown ?? 0;
  // Raw, unclamped (see the cart CoveragePanel): classified + unmapped + unknown + pending
  // == total is the bucket invariant; a negative pending means a double-count worth seeing,
  // not something to hide behind max(0, ...).
  const pending = total - classified - unmapped - unknown;
  const denom = Math.max(1, total);
  const pct = (n: number) => `${((n / denom) * 100).toFixed(1)}%`;
  const mapped = classified + unmapped; // SKUs the model gave a verdict on
  const fitPct = mapped > 0 ? (classified / mapped) * 100 : null;
  const fitTone = fitPct == null ? "idle" : fitPct >= 80 ? "ok" : fitPct >= 50 ? "warn" : "crit";
  const rows: { label: string; tone: string; n: number }[] = [
    { label: "classified (mapped to a leaf)", tone: "ok", n: classified },
    ...(unmapped > 0 ? [{ label: "unmapped (no-fit leaf)", tone: "info", n: unmapped }] : []),
    ...(unknown > 0 ? [{ label: "unknown (model omitted - retried next run)", tone: "warn", n: unknown }] : []),
    { label: "pending", tone: "idle", n: pending },
  ];
  return (
    <Panel style={{ gridColumn: "span 6" }}>
      <Panel.Head title="Classification breakdown">
        <div className="flex-1" />
        {fitPct != null && (
          <Badge variant={toneVariant(fitTone as Tone)} style={{ fontSize: 10.5 }} title="classified / (classified + unmapped)">
            {fitPct.toFixed(0)}% fit
          </Badge>
        )}
        <span className="num faint" style={{ fontSize: 11, marginLeft: 8 }}>{fmtInt(total)} SKUs</span>
      </Panel.Head>
      <Panel.Body>
        <div className="stack" style={{ height: 12, marginBottom: 12 }}>
          <i className="ok" style={{ width: pct(classified) }} />
          <i className="info" style={{ width: pct(unmapped) }} />
          <i className="warn" style={{ width: pct(unknown) }} />
          <i className="idle" style={{ width: pct(pending) }} />
        </div>
        <table className="tbl">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
                  </span>
                </td>
                <td className="r num strong">{fmtInt(r.n)}</td>
                <td className="r faint num">{pct(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          {fmtInt(stats.rows_emitted)} labels written{live ? " so far" : ""}. Labels land in a
          ClickHouse table keyed by the taxonomy fingerprint; a manual override always wins.
        </p>
      </Panel.Body>
    </Panel>
  );
}

type ChurnRow = { label: string; tone: string; n: number | undefined };

// Raw request/response captures kept vs dropped by per-error-class sampling, for the
// 1-week *_cart_debug / *_serviceability_debug tables. Rendered because the table alone
// is misleading: a run that was blocked 94,691 times keeps ~145 captures, and without
// this the next reader counts the rows and concludes it was blocked 145 times. Undefined
// on runs that predate sampling, which read as "not sampled" and render nothing.
function debugCaptureRows(stats: CartProgress): ChurnRow[] | null {
  const kept = stats.debug_captured;
  const dropped = stats.debug_sampled_out;
  if (kept === undefined && dropped === undefined) return null;
  return [
    { label: "captured (kept)", tone: "ok", n: kept },
    { label: "sampled out", tone: "idle", n: dropped },
  ];
}

const DEBUG_CAPTURE_HINT =
  "Failed fetches carry a full request/response capture. The first 50 of each " +
  "(source, outcome, status) class are stored whole, then 1-in-1000, so a rare failure is " +
  "kept intact while a repeating one can't fill the debug table. The group total is the " +
  "real number of captures the run saw.";

// A labelled group of churn rows: header (label + subtotal) then the rows.
function ChurnGroup({ label, hint, rows }: { label: string; hint: string; rows: ChurnRow[] }) {
  const total = rows.reduce((a, r) => a + (r.n ?? 0), 0);
  return (
    <>
      <tr style={{ borderTop: "1px solid var(--line)" }}>
        <td style={{ paddingTop: 9, paddingBottom: 3 }} title={hint}>
          <span className="eyebrow" style={{ fontSize: 9.5 }}>{label}</span>
        </td>
        <td className="r num faint" style={{ paddingTop: 9, paddingBottom: 3, fontSize: 10.5 }}>{fmtInt(total)}</td>
      </tr>
      {rows.map((r) => (
        <tr key={r.label}>
          <td>
            <span className="row">
              <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
            </span>
          </td>
          <td className="r num strong">{fmtInt(r.n)}</td>
        </tr>
      ))}
    </>
  );
}

// Per-attempt churn, one blocked batch retries on fresh proxies many times, so
// these counters routinely exceed the batch count. NOT failures-per-batch. Grouped
// by attribution: target-side refusals leave the proxy's score alone (the tunnel
// delivered the request); proxy-side faults score the IP down and trip cooldown.
// Shared by the cart flows (cart_inventory / store_sku_map) and brand sweep: both emit
// the same ``batches_*`` outcome counters. ``unit`` names the retried work-item in the
// copy (a cart "batch" vs a brand "request"). The served (ok) group leads so the panel
// shows successes alongside failures, not just churn.
function ChurnPanel({ stats, unit = "batch" }: { stats: CartProgress; unit?: string }) {
  // "Served" is every attempt the target answered definitively, so a not-serviceable
  // verdict belongs here: the tunnel worked and the answer is usable. Cart-only; brand
  // never emits it, and an absent key reads as 0.
  const okRows: ChurnRow[] = [
    { label: "ok (parsed)", tone: "ok", n: stats.batches_ok },
    { label: "not serviceable (clean verdict)", tone: "ok", n: stats.batches_not_serviceable },
  ];
  const targetRows: ChurnRow[] = [
    { label: "blocked (403/WAF)", tone: "warn", n: stats.batches_blocked },
    { label: "rate-limited (429)", tone: "warn", n: stats.batches_rate_limited },
    { label: "other 4xx", tone: "warn", n: stats.batches_other_4xx },
  ];
  const proxyFaultRows: ChurnRow[] = [
    { label: "transport error", tone: "crit", n: stats.batches_transport_error },
    { label: "timeout", tone: "crit", n: stats.batches_timeout },
  ];
  // Its own group, not "proxy-side": a 200 that carried no cart is the target shedding
  // load, and the pool scores it neutral and returns the IP to service without a
  // cooldown. Listing it under "scored against the IP" would say the opposite.
  const targetShedRows: ChurnRow[] = [
    { label: "empty / poisoned", tone: "idle", n: stats.batches_empty },
  ];
  // Its own group, above lease churn: a proxy-provider denial is neither the target's
  // doing nor the IP's, and it is the one number here that means "stop and go fix the
  // proxy vendor account".
  const proxyProviderRows: ChurnRow[] = [
    {
      label: "proxy provider denied (402/407)",
      tone: "crit",
      n: stats.batches_proxy_provider_denied,
    },
  ];
  const leaseRows: ChurnRow[] = [
    { label: "requeued", tone: "violet", n: stats.batches_requeued },
    // A batch that SUCCEEDED while the target refused the session part-way, standing the
    // worker down. Not in the attempts sum -- it is already counted once as an OK, and
    // counting it again would inflate the denominator goodput is measured against. Read it
    // as "this run is above the target's ceiling", not as a proxy problem.
    { label: "session refused mid-batch", tone: "warn", n: stats.batches_session_spent },
    { label: "proxy rotations", tone: "ok", n: stats.rotations },
    { label: "acquire denied", tone: "warn", n: stats.acquire_denied },
    // Deliberately beside "acquire denied" and never folded into it: a denial is a starved
    // pool, this is a full pool whose every IP refused to prime. Conflated, a run reads as
    // proxy-starved while the pool sits idle.
    { label: "no primeable IP", tone: "warn", n: stats.prime_unavailable },
    // Never reached the network (no callable input), so it is bookkeeping rather than an
    // outcome - but it has to appear somewhere or the attempt total quietly loses it.
    { label: "skipped (never sent)", tone: "idle", n: stats.batches_skipped },
  ];
  const debugRows = debugCaptureRows(stats);
  const sum = (rs: ChurnRow[]) => rs.reduce((a, r) => a + (r.n ?? 0), 0);
  const attempts =
    sum(okRows) +
    sum(targetRows) +
    sum(proxyFaultRows) +
    sum(targetShedRows) +
    sum(proxyProviderRows) +
    (stats.batches_requeued ?? 0) +
    (stats.batches_skipped ?? 0);
  // Goodput vs util (planning/23): the fraction of network attempts that produced a usable
  // result (excludes requeues, a retry marker not an outcome). Block rate is the target-
  // refusal share. Surfaces "busy but burning requests" (e.g. Instamart ~71%/28% blocks
  // behind the same 99% worker util as a healthy Zepto ~97%/1.5%).
  // Proxy-provider denials stay out of this denominator on purpose: the request never
  // reached the target, so counting it would report a billing outage at our proxy vendor
  // as a scraping failure.
  // ``skipped`` is out of this denominator for the same reason as a provider denial:
  // no request was sent, so it is not evidence about how the scraping went.
  const outcomes = sum(okRows) + sum(targetRows) + sum(proxyFaultRows) + sum(targetShedRows);
  const goodputPct = outcomes > 0 ? Math.round((sum(okRows) / outcomes) * 100) : null;
  const blockPct =
    outcomes > 0
      ? Math.round((((stats.batches_blocked ?? 0) + (stats.batches_rate_limited ?? 0)) / outcomes) * 100)
      : null;
  const goodTone = goodputPct == null ? "faint" : goodputPct >= 90 ? "ok" : goodputPct >= 70 ? "warn" : "crit";
  return (
    <Panel style={{ gridColumn: "span 4" }}>
      <Panel.Head title="Retry churn - attempts">
        <div className="flex-1" />
        {goodputPct != null && (
          <Badge
            variant={toneVariant(goodTone as Tone)}
            style={{ fontSize: 10.5, marginRight: 10 }}
            title="Goodput = usable results / network attempts (not the flattering worker-util number). Block rate = target refusals (403/429). Watch goodput, not util."
          >
            goodput {goodputPct}% - blocks {blockPct}%
          </Badge>
        )}
        <span className="num faint" style={{ fontSize: 11 }}>{fmtInt(attempts)} events</span>
      </Panel.Head>
      <Panel.Body>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 0, marginBottom: 10, lineHeight: 1.5 }}>
          Cumulative request attempts, a blocked {unit} retries on a fresh proxy, so
          these exceed the {unit} count. Grouped by who's at fault.
        </p>
        <table className="tbl">
          <tbody>
            <ChurnGroup
              label="served - request parsed"
              hint={`A 2xx the adapter parsed into a result. The ${unit} succeeded on this attempt.`}
              rows={okRows}
            />
            <ChurnGroup
              label="target-side - proxy not penalised"
              hint="The proxy delivered the request; the platform refused it. Excluded from the proxy's success score."
              rows={targetRows}
            />
            <ChurnGroup
              label="proxy-side - scored against the IP"
              hint="A dead tunnel or a slow proxy. Lowers the IP's score and trips cooldown / dead-park."
              rows={proxyFaultRows}
            />
            <ChurnGroup
              label="target load-shed - proxy not penalised"
              hint="A 2xx that carried no cart. The tunnel worked and the target answered with nothing, which proven-good IPs draw as often as fresh ones, so the pool returns the IP to service with no cooldown."
              rows={targetShedRows}
            />
            {(stats.batches_proxy_provider_denied ?? 0) > 0 && (
              <ChurnGroup
                label="proxy provider - account refused"
                hint="Our proxy provider (Webshare) refused to open the tunnel: 402 = out of credit or over the plan bandwidth cap, 407 = rejected credentials. The whole fleet shares one account, so no IP can clear this; the pool suspends leasing and the run fails fast."
                rows={proxyProviderRows}
              />
            )}
            <ChurnGroup label="lease churn" hint="Retry & lease bookkeeping." rows={leaseRows} />
            {debugRows && (
              <ChurnGroup label="debug capture - sampled" hint={DEBUG_CAPTURE_HINT} rows={debugRows} />
            )}
          </tbody>
        </table>
      </Panel.Body>
    </Panel>
  );
}

// Merchant-sweep retry churn: one probe attempt per row, bucketed by ServiceabilityOutcome.
// A serviceability probe that fails (UNKNOWN empty / block / timeout / ...) is requeued on
// a fresh proxy, so attempts exceed the unique-cell count. Grouped by attribution, mirroring
// the cart ChurnPanel: target-side refusals leave the proxy's score alone; proxy-side faults
// score the IP down. UNKNOWN is Instamart's ambiguous card-less 200 (never cached, retried).
function MerchantChurnPanel({ stats }: { stats: CartProgress }) {
  const a = stats.attempts ?? {};
  const okRows: ChurnRow[] = [{ label: "ok (served)", tone: "ok", n: a.ok }];
  const ambiguousRows: ChurnRow[] = [
    { label: "unknown (empty card)", tone: "idle", n: a.unknown },
  ];
  const targetRows: ChurnRow[] = [
    { label: "blocked (403/WAF)", tone: "warn", n: a.blocked },
    { label: "rate-limited (429)", tone: "warn", n: a.rate_limited },
    { label: "other 4xx", tone: "warn", n: a.other_4xx },
  ];
  const proxyFaultRows: ChurnRow[] = [
    { label: "transport error", tone: "crit", n: a.transport_error },
    { label: "timeout", tone: "crit", n: a.timeout },
  ];
  // Its own group, as on the cart/brand panel: a proxy-provider denial is neither the
  // target's doing nor the IP's, and it is the one number here that means "stop and go
  // fix the proxy vendor account". Without the row a merchant run's denials appear
  // nowhere while the header's attempt total still counts them.
  const proxyProviderRows: ChurnRow[] = [
    { label: "proxy provider denied (402/407)", tone: "crit", n: a.proxy_provider_denied },
  ];
  const leaseRows: ChurnRow[] = [
    { label: "requeued", tone: "violet", n: stats.retries },
    { label: "gave up (exhausted)", tone: "crit", n: stats.probe_errors },
    { label: "proxy rotations", tone: "ok", n: stats.rotations },
    { label: "acquire denied", tone: "warn", n: stats.acquire_denied },
  ];
  const debugRows = debugCaptureRows(stats);
  const attempts = Object.values(a).reduce((s, n) => s + (n ?? 0), 0);
  return (
    <Panel style={{ gridColumn: "span 4" }}>
      <Panel.Head title="Retry churn - attempts">
        <div className="flex-1" />
        <span className="num faint" style={{ fontSize: 11 }}>{fmtInt(attempts)} probes</span>
      </Panel.Head>
      <Panel.Body>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 0, marginBottom: 10, lineHeight: 1.5 }}>
          Live probe attempts by outcome, a failed probe retries on a fresh proxy, so these
          exceed the unique-cell count. Grouped by who's at fault.
        </p>
        <table className="tbl">
          <tbody>
            <ChurnGroup label="served" hint="A 2xx with a serviceability card (serviceable or a genuine not-serviceable verdict). Cached." rows={okRows} />
            <ChurnGroup
              label="ambiguous - retried, never cached"
              hint="A 2xx with no card: Instamart intermittently drops the card for a serviceable coord. Treated as a retryable failure, not a verdict."
              rows={ambiguousRows}
            />
            <ChurnGroup
              label="target-side - proxy not penalised"
              hint="The proxy delivered the request; the platform refused it. Excluded from the proxy's success score."
              rows={targetRows}
            />
            <ChurnGroup
              label="proxy-side - scored against the IP"
              hint="A dead tunnel or a slow proxy. Lowers the IP's score and trips cooldown / dead-park."
              rows={proxyFaultRows}
            />
            <ChurnGroup
              label="proxy vendor - nothing the pool can fix"
              hint="Our proxy provider refused the CONNECT (402 out of credit / bandwidth cap, 407 bad credentials). True of every IP on the account at once, so it is unscored and no rotation clears it."
              rows={proxyProviderRows}
            />
            <ChurnGroup label="lease churn" hint="Retry, giveup & lease bookkeeping." rows={leaseRows} />
            {debugRows && (
              <ChurnGroup label="debug capture - sampled" hint={DEBUG_CAPTURE_HINT} rows={debugRows} />
            )}
          </tbody>
        </table>
      </Panel.Body>
    </Panel>
  );
}

// Brand-sweep discovery: search + PDP. Pipelined: each product's PDP enrich is
// enqueued at first-seen and runs on the same worker pool as search (planning/23 [09]).
function SweepStagePanel({ stats, live }: { stats: CartProgress; live: boolean }) {
  const searchesTotal = stats.searches_total ?? 0;
  // (store x candidate) units: the planned search work (~= brands x stores). Each unit
  // is done only once its whole pagination chain is swept; failed units never finish.
  const unitsTotal = stats.search_units_total ?? 0;
  const unitsOk = stats.search_units_ok ?? 0;
  const unitsFailed = stats.search_units_failed ?? 0;
  const pdpsTotal = stats.pdps_total ?? 0;
  const pdpsDone = stats.pdps_done ?? 0;
  // Search-only platforms (Instamart) have no PDP enrich stage: hide the
  // PDP bar/rows and retitle the panel to just "search".
  const hasPdpStage = stats.has_pdp_stage !== false;
  const pct = (n: number, d: number) => (d > 0 ? Math.min(100, (n / d) * 100) : 0);
  const rows: { label: string; tone: string; n: number | undefined }[] = [
    { label: "candidate searches finished", tone: "ok", n: unitsOk },
    { label: "candidate searches failed", tone: "crit", n: unitsFailed },
    { label: "search requests (incl. pages + retries)", tone: "info", n: searchesTotal },
    { label: "products discovered", tone: "info", n: stats.products_discovered },
    ...(hasPdpStage
      ? [
          { label: "PDPs enriched", tone: "ok", n: stats.pdps_done },
          { label: "PDPs failed", tone: "crit", n: stats.pdps_failed },
        ]
      : []),
  ];
  return (
    <Panel style={{ gridColumn: "span 5" }}>
      <Panel.Head title={hasPdpStage ? "Discovery - search -> PDP" : "Discovery - search"}>
        <div className="flex-1" />
        <span className="num faint" style={{ fontSize: 11 }}>{fmtInt(unitsTotal)} candidates</span>
      </Panel.Head>
      <Panel.Body>
        <div className="col" style={{ gap: 4, marginBottom: 12 }}>
          <div className="row faint" style={{ fontSize: 10.5, justifyContent: "space-between" }}>
            <span>candidate searches (store &times; candidate)</span>
            <span className="num">{fmtInt(unitsOk)}/{fmtInt(unitsTotal)}</span>
          </div>
          <div className="bar">
            <span className={`ok${live ? " stream" : ""}`} style={{ width: `${pct(unitsOk, unitsTotal)}%` }} />
            {unitsFailed > 0 && (
              <span
                className="crit"
                style={{ width: `${pct(unitsFailed, unitsTotal)}%`, left: `${pct(unitsOk, unitsTotal)}%` }}
              />
            )}
          </div>
          {hasPdpStage && (
            <>
              <div className="row faint" style={{ fontSize: 10.5, justifyContent: "space-between", marginTop: 6 }}>
                <span>PDP enrich stage</span>
                <span className="num">{fmtInt(pdpsDone)}/{fmtInt(pdpsTotal)}</span>
              </div>
              <div className="bar">
                <span className={`ok${live ? " stream" : ""}`} style={{ width: `${pct(pdpsDone, pdpsTotal)}%` }} />
              </div>
            </>
          )}
        </div>
        <table className="tbl">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
                  </span>
                </td>
                <td className="r num strong">{fmtInt(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          A candidate search = one (store &times; search term); it finishes only when its
          whole pagination chain is swept.
          {hasPdpStage
            ? " Pipelined: each product's PDP is enriched as soon as it is first discovered (once per unique product, store-agnostic), on the same worker pool as search."
            : " Search carries every catalog field (brand, category, pack, price, stock), so there is no separate PDP stage."}
        </p>
      </Panel.Body>
    </Panel>
  );
}

function CacheBar({ label, hit, miss }: { label: string; hit: number; miss: number }) {
  const total = hit + miss;
  const rate = total > 0 ? (hit / total) * 100 : null;
  return (
    <div className="col" style={{ gap: 3 }}>
      <div className="row faint" style={{ fontSize: 10.5, justifyContent: "space-between" }}>
        <span>{label}</span>
        <span className="num">{rate == null ? "-" : `${rate.toFixed(0)}% hit`}</span>
      </div>
      <div className="stack" style={{ height: 9 }}>
        <i className="ok" style={{ width: `${total > 0 ? (hit / total) * 100 : 0}%` }} />
        <i className="idle" style={{ width: `${total > 0 ? (miss / total) * 100 : 0}%` }} />
      </div>
      <span className="faint num" style={{ fontSize: 10 }}>{fmtInt(hit)} hit - {fmtInt(miss)} miss</span>
    </div>
  );
}

// Read-through cache hit-rate (the Phase-6 payoff: re-runs reuse cached bodies) +
// SKU dimension churn (added / reactivated / removed) from compaction.
function SweepCachePanel({ stats, mode }: { stats: CartProgress; mode: string }) {
  // Additive run (planning/23 [08] B2): a SAMPLED run that didn't retire this time
  // (skus_removed 0 by design), so render it as an info note, not an alarming warn row.
  // Keyed on the effective mode (not stats.remove_missing, which defaults false until
  // compaction so a *live full* run would briefly mislabel). A sampled run that DID retire
  // via the rolling window has remove_missing true -> shows the real removed count.
  const additive = mode === "sampled" && stats.remove_missing !== true;
  const churn: { label: string; tone: string; n: number | undefined; note?: string }[] = [
    { label: "SKUs added", tone: "ok", n: stats.skus_added },
    { label: "SKUs reactivated", tone: "info", n: stats.skus_reactivated },
    additive
      ? { label: "removals deferred", tone: "info", n: undefined, note: "rolling window / Full run" }
      : { label: "SKUs removed", tone: "warn", n: stats.skus_removed },
    { label: "proxy rotations", tone: "ok", n: stats.rotations },
  ];
  return (
    <Panel style={{ gridColumn: "span 4" }}>
      <Panel.Head title="Read-through cache - churn" />
      <Panel.Body className="flex flex-col gap-3">
        <CacheBar label="search cache (1wk TTL)" hit={stats.cache_search_hits ?? 0} miss={stats.cache_search_misses ?? 0} />
        <CacheBar label="PDP cache (1mo TTL)" hit={stats.cache_pdp_hits ?? 0} miss={stats.cache_pdp_misses ?? 0} />
        <table className="tbl" style={{ borderTop: "1px solid var(--line)" }}>
          <tbody>
            {churn.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
                  </span>
                </td>
                <td className="r num strong">
                  {r.note ? <span className="faint" style={{ fontSize: 10.5 }}>{r.note}</span> : fmtInt(r.n)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel.Body>
    </Panel>
  );
}

function MerchantPanel({ stats, live }: { stats: CartProgress; live: boolean }) {
  const cellsTotal = stats.cells_total ?? 0;
  // Progress + shares use cells *successfully* probed (once each), not cells_probed (which
  // counts every attempt incl. retries and overflows cells_total on a high-retry sweep).
  const cellsSucceeded = stats.cells_succeeded ?? 0;
  const serviceable = stats.cells_serviceable ?? 0;
  const pct = (n: number, d: number) => (d > 0 ? Math.min(100, (n / d) * 100) : 0);
  const anchoring = stats.stage === "anchoring";
  const rows: { label: string; tone: string; n: number | undefined }[] = [
    { label: "cells serviceable", tone: "ok", n: stats.cells_serviceable },
    { label: "cells from cache", tone: "info", n: stats.cells_cached },
    { label: "probe errors", tone: "crit", n: stats.probe_errors },
    { label: "store sightings", tone: "info", n: stats.sightings },
    { label: "stores added", tone: "ok", n: stats.stores_added },
    { label: "proxy rotations", tone: "ok", n: stats.rotations },
  ];
  return (
    <Panel style={{ gridColumn: "span 5" }}>
      <Panel.Head title="Geo cascade - serviceability">
        <div className="flex-1" />
        <span className="num faint" style={{ fontSize: 11 }}>
          {stats.stage ? `stage ${stats.stage}` : ""}
          {stats.stage === "probing" && stats.phase ? ` - phase ${stats.phase}` : ""}
        </span>
      </Panel.Head>
      <Panel.Body>
        {anchoring && (
          <Row gap="sm" style={{ marginBottom: 10, fontSize: 11 }}>
            {live && <Dot live />}
            <span className="num" style={{ color: "var(--info)" }}>
              Anchoring: generating F3 anchors &amp; compacting the dimension over the
              sighting window ({fmtInt(stats.stores_found)} stores)
            </span>
          </Row>
        )}
        <div className="col" style={{ gap: 4, marginBottom: 12 }}>
          <div className="row faint" style={{ fontSize: 10.5, justifyContent: "space-between" }}>
            <span>cells probed</span>
            <span className="num">{fmtInt(cellsSucceeded)}/{fmtInt(cellsTotal)}</span>
          </div>
          <div className="bar">
            <span className={`ok${live ? " stream" : ""}`} style={{ width: `${pct(cellsSucceeded, cellsTotal)}%` }} />
          </div>
          <div className="row faint" style={{ fontSize: 10.5, justifyContent: "space-between", marginTop: 6 }}>
            <span>serviceable share</span>
            <span className="num">{fmtInt(serviceable)}/{fmtInt(cellsSucceeded)}</span>
          </div>
          <div className="bar">
            <span className="info" style={{ width: `${pct(serviceable, cellsSucceeded)}%` }} />
          </div>
        </div>
        <table className="tbl">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
                  </span>
                </td>
                <td className="r num strong">{fmtInt(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          Coarse-to-dense cascade: each phase densifies only inside the serviceable cells of
          the one before. Probes are read-through cached, so a same-week re-sweep is near-free.
        </p>
      </Panel.Body>
    </Panel>
  );
}

function MerchantCachePanel({ stats }: { stats: CartProgress }) {
  const cached = stats.cells_cached ?? 0; // cross-run 1wk cache hits
  const reused = stats.cells_reused ?? 0; // in-run dedupe (serviceable parent re-hit)
  // Cells that needed a fresh network probe (neither a cross-run hit nor an in-run reuse).
  const liveProbes = Math.max(0, (stats.cells_probed ?? 0) - cached - reused);
  const churn: { label: string; tone: string; n: number | undefined }[] = [
    { label: "stores added", tone: "ok", n: stats.stores_added },
    { label: "stores reactivated", tone: "info", n: stats.stores_reactivated },
    { label: "stores candidate/closed", tone: "warn", n: stats.stores_removed },
    { label: "cross-run cache hits", tone: "info", n: cached },
    { label: "in-run reuse (dedupe)", tone: "violet", n: reused },
  ];
  return (
    <Panel style={{ gridColumn: "span 4" }}>
      <Panel.Head title="Serviceability cache - churn" />
      <Panel.Body className="flex flex-col gap-3">
        <CacheBar
          label="serviceability cache (1wk TTL)"
          hit={cached}
          miss={liveProbes}
        />
        <table className="tbl" style={{ borderTop: "1px solid var(--line)" }}>
          <tbody>
            {churn.map((r) => (
              <tr key={r.label}>
                <td>
                  <span className="row">
                    <Dot style={{ background: dotColor(r.tone) }} /> {r.label}
                  </span>
                </td>
                <td className="r num strong">{fmtInt(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel.Body>
    </Panel>
  );
}

// Resume cache (cart flows: cart_inventory / store_sku_map), the per-run read-through
// cache that lets a Temporal RETRY skip (store, SKU) pairs a prior attempt already
// resolved instead of re-probing them. Unlike brand's search/PDP body caches (cross-run,
// the re-run payoff) this is resume-only: it pays off only when a run is retried
// mid-flight, so a clean first attempt shows 0 hits. The carriage flow disables it
// outright, a fresh full re-probe each run keeps verdicts current.
function ResumeCachePanel({
  stats,
  carriage,
  rank = false,
}: {
  stats: CartProgress;
  carriage: boolean;
  rank?: boolean;
}) {
  const total = (rank ? stats.probes_planned : stats.store_sku_total) ?? 0;
  const hit = (rank ? stats.probes_resumed : stats.store_sku_resumed) ?? 0;
  const miss = Math.max(0, total - hit);
  return (
    <Panel style={{ gridColumn: "span 4" }}>
      <Panel.Head title="Resume cache">
        <div className="flex-1" />
        <span className="num faint" style={{ fontSize: 11 }}>
          {carriage ? "disabled" : "per-run - retry-only"}
        </span>
      </Panel.Head>
      <Panel.Body className="flex flex-col gap-3">
        {carriage ? (
          <p className="faint" style={{ fontSize: 10.5, margin: 0, lineHeight: 1.5 }}>
            Disabled for carriage, the StorexSKU map re-probes every (store, SKU) pair each
            run so a verdict is never stale, so nothing is restored from cache.
          </p>
        ) : (
          <>
            <CacheBar label="resume cache (per-run)" hit={hit} miss={miss} />
            <p className="faint" style={{ fontSize: 10.5, margin: 0, lineHeight: 1.5 }}>
              Restores {rank ? "probes" : "(store, SKU) pairs"} a prior attempt of{" "}
              <i>this</i> run already resolved, so a deploy-kill retry doesn't re-ask them.
              A clean first attempt shows 0 hits; a resumed run shows what it skipped.
            </p>
          </>
        )}
      </Panel.Body>
    </Panel>
  );
}

function ProxyPanel({ platform }: { platform: string }) {
  const summaryQuery = useSummaryV1ProxySummaryGet({
    query: { refetchInterval: 4000 },
  });
  // /v1/proxy/summary is an opaque dict; cast to the local shape this panel reads.
  const summary = summaryQuery.data as ProxySummary | undefined;
  const p = summary?.platforms.find((x) => x.platform === platform);
  if (!p) {
    return (
      <Panel style={{ gridColumn: "span 3" }}>
        <Panel.Head title={`Proxy - ${platform}`} />
        <Panel.Body className="muted">No pool data.</Panel.Body>
      </Panel>
    );
  }
  const zone = p.pressure >= 1 ? "crit" : p.pressure >= 0.8 ? "warn" : "ok";
  const denom = Math.max(1, p.total);
  const w = (n: number) => `${((n / denom) * 100).toFixed(1)}%`;
  const cooling = p.counts.cooling ?? 0;
  const dead = p.counts.dead ?? 0;
  return (
    <Panel style={{ gridColumn: "span 3" }}>
      <Panel.Head title={`Proxy - ${platform}`}>
        <div className="flex-1" />
        <Tag asChild>
          <Link to="/proxies" style={{ textDecoration: "none" }}>open <ExternalLink size={11} aria-hidden /></Link>
        </Tag>
      </Panel.Head>
      <Panel.Body className="flex flex-col gap-3">
        <div className="col" style={{ gap: 2 }}>
          <span className="eyebrow">pressure</span>
          <span
            className="display"
            style={{ fontSize: 32, color: `var(--${zone})`, fontVariationSettings: "'wdth' 115,'wght' 800" }}
          >
            {p.pressure.toFixed(2)}
          </span>
          <span
            className="faint"
            style={{ fontSize: 10.5, display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            demand / capacity <InfoDot title={PRESSURE_HELP} />
          </span>
        </div>
        <div className="stack">
          <i className="ok" style={{ width: w(p.leased) }} />
          <i className="warn" style={{ width: w(cooling) }} />
          <i className="crit" style={{ width: w(dead) }} />
          <i className="idle" style={{ width: w(p.free) }} />
        </div>
        <div className="kv" style={{ borderTop: "1px solid var(--line)", paddingTop: 10 }}>
          <dt>leased</dt>
          <dd style={{ color: "var(--ok)" }}>{p.leased}</dd>
          <dt>free</dt>
          <dd style={p.free === 0 ? { color: "var(--crit)" } : undefined}>{p.free}</dd>
          <dt>acquire p99</dt>
          <dd>{Math.round(p.acquire_p99_ms)} ms</dd>
        </div>
      </Panel.Body>
    </Panel>
  );
}

function LifecyclePanel({ run, status }: { run: Run; status: RunStatus }) {
  const done = (s: RunStatus[]) => s.includes(status) || isTerminal(status);
  const steps: { key: string; label: string; reached: boolean; at: string | null }[] = [
    { key: "queued", label: "queued", reached: true, at: run.created_at },
    { key: "claimed", label: "claimed", reached: status !== "queued", at: run.started_at },
    { key: "running", label: "running", reached: done(["running"]), at: run.started_at },
    { key: "heartbeat", label: "heartbeat", reached: run.heartbeat_at != null, at: run.heartbeat_at },
    {
      key: "terminal",
      label: isTerminal(status) ? status : "finished",
      reached: isTerminal(status),
      at: run.finished_at,
    },
  ];
  const live = !isTerminal(status);
  return (
    <Panel style={{ gridColumn: "span 5" }}>
      <Panel.Head title="Lifecycle" />
      <Panel.Body className="feed" style={{ fontFamily: "var(--f-mono)", fontSize: 11.5 }}>
        {steps.map((s) => {
          const isHeartbeat = s.key === "heartbeat";
          return (
            <div className="ln" key={s.key} style={s.reached ? undefined : { color: "var(--ink-4)" }}>
              <Dot tone={s.reached && !(isHeartbeat && live) ? "ok" : undefined} live={s.reached && isHeartbeat && live} />
              <b style={s.reached ? (isHeartbeat ? { color: "var(--ok)" } : undefined) : { color: "var(--ink-4)", fontWeight: 400 }}>
                {s.label}
              </b>
              {s.key === "claimed" && run.claimed_by && (
                <span className="faint">{run.claimed_by}</span>
              )}
              <time style={{ marginLeft: "auto" }}>{s.reached ? fmtTime(s.at) : "-"}</time>
            </div>
          );
        })}
      </Panel.Body>
    </Panel>
  );
}

// -- helpers ------------------------------------------------------------------
function useJob(jobId: string | undefined): Job | undefined {
  const q = useGetJobV1JobsJobIdGet(jobId ?? "", {
    query: { enabled: !!jobId },
  });
  return q.data as Job | undefined;
}


function dotColor(tone: string): string {
  if (tone === "violet") return "var(--violet)";
  if (tone === "idle") return "var(--line-2)";
  return `var(--${tone})`;
}

// Completed-run, per-store coverage. One row per store (location + tier + per-bucket
// SKU counts); click a row to lazy-load the inline per-SKU detail (which SKUs were
// delisted / deduped / gave-up and why).
const COVER_COLS: { key: "delisted" | "deduped" | "gave_up"; label: string; tone: string }[] = [
  { key: "delisted", label: "delisted", tone: "info" },
  { key: "deduped", label: "deduped", tone: "violet" },
  { key: "gave_up", label: "gave up", tone: "crit" },
];
const STATUS_META: Record<string, { label: string; tone: string }> = {
  delisted: { label: "delisted", tone: "info" },
  deduped: { label: "deduped", tone: "violet" },
  gave_up: { label: "gave up", tone: "crit" },
  // store_sku_map (carriage) drill statuses
  estimated: { label: "reroute-estimated", tone: "idle" },
};

function StoreCoveragePanel({
  runId,
  platform,
  target,
}: {
  runId: string;
  platform: Platform | null;
  target: number;
}) {
  const q = useRunStoreCoverageV1RunsRunIdStoreCoverageGet(runId, {
    query: { staleTime: 60_000 },
  });
  const [open, setOpen] = useState<string | null>(null);
  const data = q.data;
  const totals = data?.totals;
  // Distinct stores that actually served (covered >=1 SKU) vs total stores probed --
  // the headline "did the sweep reach the stores" number, separate from the per-SKU buckets.
  const totalStores = data?.stores.length ?? 0;
  const servingStores = data?.stores.filter((s) => s.covered > 0).length ?? 0;
  return (
    <Panel style={{ gridColumn: "span 12" }}>
      <Panel.Head title="Store coverage: per store, click to drill into SKUs">
        <div className="flex-1" />
        {data && (
          <Badge
            variant={toneVariant(servingStores === totalStores ? "ok" : "warn")}
            style={{ fontSize: 10.5, marginLeft: 6 }}
            title="Distinct stores that covered at least one SKU, of all stores probed this run"
          >
            {fmtInt(servingStores)} / {fmtInt(totalStores)} stores serving
          </Badge>
        )}
        {totals &&
          COVER_COLS.map((c) => (
            <Badge key={c.key} variant={toneVariant(c.tone as Tone)} style={{ fontSize: 10.5, marginLeft: 6 }}>
              {fmtInt(totals[c.key])} {c.label}
            </Badge>
          ))}
        {totals && (totals.routed_away ?? 0) > 0 && (
          <Badge variant="outline-destructive" style={{ fontSize: 10.5, marginLeft: 6 }}>
            {fmtInt(totals.routed_away)} routed away
          </Badge>
        )}
      </Panel.Head>
      <Panel.Body>
        {q.isLoading ? (
          <p className="faint" style={{ fontSize: 11.5 }}>Loading...</p>
        ) : q.isError ? (
          <p className="faint" style={{ fontSize: 11.5 }}>Could not load (is ClickHouse configured?).</p>
        ) : !data || data.stores.length === 0 ? (
          <p className="faint" style={{ fontSize: 11.5 }}>No store coverage recorded for this run.</p>
        ) : (
          <div style={{ maxHeight: "70vh", overflow: "auto" }}>
          <table className="tbl sticky-head">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>store</th>
                <th style={{ textAlign: "left" }}>location</th>
                <th style={{ textAlign: "left" }}>tier</th>
                <th className="r" title="SKUs that should have been covered (run's SKU universe)">should cover</th>
                <th className="r">covered</th>
                {COVER_COLS.map((c) => (
                  <th key={c.key} className="r">
                    <span className="row" style={{ justifyContent: "flex-end" }}>
                      <Dot style={{ background: dotColor(c.tone) }} /> {c.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.stores.map((s) => {
                const isOpen = open === s.store_id;
                const loc = [s.locality, s.city].filter(Boolean).join(", ");
                const routedAway = s.status === "routed_away";
                return (
                  <Fragment key={s.store_id}>
                    <tr
                      onClick={() => setOpen(isOpen ? null : s.store_id)}
                      style={{ cursor: "pointer", opacity: routedAway ? 0.85 : 1 }}
                    >
                      <td>
                        <span className="row" style={{ flexWrap: "wrap", gap: 6 }}>
                          <span className="faint" style={{ width: 12, display: "inline-block" }}>
                            {isOpen ? <ChevronDown size={11} aria-hidden /> : <ChevronRight size={11} aria-hidden />}
                          </span>
                          {s.name || s.store_id}
                          {platform !== null ? (
                            <Link
                              to={`/stores/${platform}/${encodeURIComponent(s.store_id)}`}
                              className="faint mono"
                              style={{ fontSize: 10 }}
                              onClick={(e) => e.stopPropagation()}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open store page"
                            >
                              {s.store_id}
                            </Link>
                          ) : (
                            <span className="faint mono" style={{ fontSize: 10 }}>{s.store_id}</span>
                          )}
                          {routedAway && (
                            <Badge
                              variant="outline-destructive"
                              style={{ fontSize: 9.5 }}
                              title="This store's anchor was routed to another store (coverage overlap); its own inventory was not captured."
                            >
                              <CornerDownRight size={11} aria-hidden /> routed to {s.routed_to_name || s.routed_to_id}
                              {platform !== null && s.routed_to_id ? (
                                <Link
                                  to={`/stores/${platform}/${encodeURIComponent(s.routed_to_id)}`}
                                  className="mono"
                                  style={{ opacity: 0.8, marginLeft: 4, color: "inherit" }}
                                  onClick={(e) => e.stopPropagation()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open store page"
                                >
                                  {s.routed_to_id}
                                </Link>
                              ) : (
                                <span className="mono" style={{ opacity: 0.7, marginLeft: 4 }}>{s.routed_to_id}</span>
                              )}
                            </Badge>
                          )}
                        </span>
                      </td>
                      <td className="faint">{loc || "-"}</td>
                      <td><TierPills tiers={s.tiers} fallback={s.tier} /></td>
                      <td className="r num faint">{target ? fmtInt(target) : "-"}</td>
                      <td className="r num faint">{routedAway ? <span className="crit">not captured</span> : fmtInt(s.covered)}</td>
                      {COVER_COLS.map((c) => (
                        <td key={c.key} className="r num strong">
                          {s[c.key] ? fmtInt(s[c.key]) : <span className="faint">-</span>}
                        </td>
                      ))}
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={5 + COVER_COLS.length} style={{ padding: 0 }}>
                          <div style={{ background: "var(--bg-2)", padding: "10px 12px 12px 24px" }}>
                            {routedAway && (
                              <div
                                className="mono"
                                style={{
                                  color: "var(--crit)",
                                  background: "var(--crit-dim)",
                                  border: "1px solid var(--crit-line)",
                                  borderRadius: "var(--r)",
                                  padding: "8px 10px",
                                  marginBottom: 12,
                                  fontSize: 11,
                                }}
                              >
                                Anchor routed to {s.routed_to_name || s.routed_to_id} (coverage
                                overlap), this store's own inventory was not captured this run. The
                                probe collected {s.routed_to_name || s.routed_to_id}'s catalog
                                instead. Map shows where the anchor was served from.
                              </div>
                            )}
                            {(s.deduped > 0 || routedAway) && (
                              <div style={{ marginBottom: 12 }}>
                                <div className="faint" style={{ fontSize: 11, marginBottom: 6 }}>
                                  Coverage overlap, {s.name || s.store_id}'s anchor and the stores
                                  serving its location
                                </div>
                                <StoreOverlapMap runId={runId} storeId={s.store_id} platform={platform} />
                              </div>
                            )}
                            <StoreSkuDrill
                              runId={runId}
                              store={s.name || s.store_id}
                              storeId={s.store_id}
                              platform={platform}
                              routedAway={routedAway}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
        <p className="faint" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          Sorted worst-first by coverage gap (should-cover minus covered). <b>delisted</b> = SKU
          not sold at this serviceable store - <b>deduped</b> = anchor routed to a <i>different</i>
          store's merchant, already collected (own-merchant re-reads count as covered, not deduped) -
          <b>gave up</b> = exhausted retries - <b>routed away</b> = the anchor was served by a
          different store (coverage overlap) and this store's own inventory was not captured. Counts
          are distinct SKUs per store.
        </p>
      </Panel.Body>
    </Panel>
  );
}

function StoreSkuDrill({
  runId,
  store,
  storeId,
  platform,
  routedAway = false,
}: {
  runId: string;
  store: string;
  storeId: string;
  platform: Platform | null;
  routedAway?: boolean;
}) {
  const q = useRunStoreSkuDetailV1RunsRunIdStoreCoverageStoreIdGet(runId, storeId, {
    query: { staleTime: 60_000 },
  });
  if (q.isLoading) {
    return <p className="faint" style={{ fontSize: 11 }}>Loading SKUs for {store}...</p>;
  }
  if (!q.data || q.data.skus.length === 0) {
    return (
      <p className="faint" style={{ fontSize: 11 }}>
        {routedAway
          ? `No per-SKU records for ${store}: its probe collected the routed-to store's catalog, so its own SKUs were never read.`
          : `Every SKU at ${store} got an in-stock reading.`}
      </p>
    );
  }
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>product</th>
          <th style={{ textAlign: "left" }}>pack size</th>
          <th style={{ textAlign: "left" }}>status</th>
          <th style={{ textAlign: "left" }}>reason</th>
          <th style={{ textAlign: "left" }}>overlapping store</th>
          <th style={{ textAlign: "left" }}>its tiers</th>
        </tr>
      </thead>
      <tbody>
        {q.data.skus.map((r) => {
          const m = STATUS_META[r.status] ?? { label: r.status, tone: "idle" };
          return (
            <tr key={`${r.status}:${r.product_id}`}>
              <td title={`${r.product_id}${r.sku ? ` - ${r.sku}` : ""}`}>
                {r.name || r.sku || r.product_id}
              </td>
              <td className="faint">{r.pack_size || "-"}</td>
              <td>
                <span className="row">
                  <Dot style={{ background: dotColor(m.tone) }} /> {m.label}
                </span>
              </td>
              <td className="faint" style={{ fontSize: 10.5 }}>{r.reason || "-"}</td>
              <td>
                {r.covered_by && platform !== null ? (
                  <Link
                    to={`/stores/${platform}/${encodeURIComponent(r.covered_by)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {r.covered_by_name || r.covered_by}
                    <span className="faint mono" style={{ fontSize: 10, marginLeft: 4 }}>
                      {r.covered_by}
                    </span>
                  </Link>
                ) : r.covered_by ? (
                  <span className="mono" style={{ fontSize: 10.5 }}>{r.covered_by}</span>
                ) : (
                  <span className="faint">-</span>
                )}
              </td>
              <td>
                {r.status === "deduped" ? (
                  <TierPills tiers={r.covered_by_tiers ?? undefined} fallback={r.covered_by_tier ?? undefined} />
                ) : (
                  <span className="faint">-</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ViewHeadFallback() {
  return (
    <PageHeader
      className="mb-3.5"
      title="Run monitor"
      description="Live run progress - SSE-streamed"
    />
  );
}
